<template>
  <div
    v-show="isEditing"
    id="editor-container"
    ref="editContainer"
    class="editor-container"
  >
    <img
      id="annotation-image"
      ref="annotationImage"
      :src="currentImageUrl"
      crossorigin="anonymous"
      class="annotation-image"
    />
    <v-progress-circular
      v-show="isSaving || persistencePending || annotationRestorePending"
      color="primary"
      :size="72"
      :width="5"
      indeterminate
      class="image-progress"
    ></v-progress-circular>

    <v-btn
      v-show="!showControls && !cropping"
      id="close-editing"
      color="editor-menu"
      icon="icon-close-large"
      class="close-editing mb-4 mt-4 mr-4"
      style="color: #000"
      :disabled="editorExitDisabled"
      @click="reset(false)"
    >
    </v-btn>
    <v-btn
      v-show="cropping"
      id="cancel-crop"
      color="editor-menu"
      rounded="pill"
      class="close-editing cancel-crop mb-4 mt-4 mr-4"
      style="color: #000"
      :disabled="editorExitDisabled"
      @click="reset(false)"
    >
      Cancel
    </v-btn>
    <div v-show="cropping" id="crop-container" ref="cropContainer"></div>

    <div
      id="editor-menu"
      class="d-flex"
      style="
        background-color: black;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        z-index: 100;
        position: absolute;
      "
      :style="{ display: isEditing ? 'unset' : 'none !important' }"
    >
      <div
        class="d-flex editor-menu mt-4"
        style="width: 64px; z-index: 1; position: absolute; left: 16px; top: 0"
        :class="{
          'flex-row': isMobileLandscape,
          'flex-column': !isMobileLandscape,
        }"
      >
        <v-btn
          :icon="showControls ? 'icon-close-large' : 'icon-pencil-edit-stroke'"
          id="toggle-edit"
          color="editor-menu"
          class="mb-4 close-menu"
          @click="toggleEdit"
        >
        </v-btn>

        <div
          v-show="showControls"
          style="display: flex"
          :class="{
            'flex-row': isMobileLandscape,
            'flex-column': !isMobileLandscape,
            'ml-4': isMobileLandscape,
          }"
        >
          <v-btn
            id="undo"
            color="editor-menu"
            elevation="3"
            icon="icon-arrow-undo-up-left"
            class="undo"
            :disabled="!canUndo"
            :class="{
              'rounded-t-circle': !isMobileLandscape,
              'rounded-ts-circle': isMobileLandscape,
              'rounded-bs-circle': isMobileLandscape,
              disabled: !canUndo,
            }"
            @click="undo"
          >
          </v-btn>
          <v-btn
            color="editor-menu"
            elevation="3"
            :disabled="editorInteractionDisabled"
          >
            <ColorPicker
              :model-value="annotationColor"
              @update:model-value="updateColor"
            ></ColorPicker>
          </v-btn>
          <v-btn
            id="freehand-marker"
            color="editor-menu"
            elevation="3"
            icon
            class="border-primary"
            :disabled="editorInteractionDisabled"
            :class="{
              'border-top': !isMobileLandscape,
              'border-side': isMobileLandscape,
              selected: isMarkerSelected(FreehandMarker.typeName),
            }"
            @click="freehand(true)"
          >
            <svg
              width="24"
              height="20"
              viewBox="0 0 24 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 3.46583C19.4953 3.25192 15.4982 2.07439 13.085 2.66322C12.1037 2.90263 10.7861 5.79369 11.6081 6.35521C12.6433 7.06248 13.2961 8.11937 14.7498 8.74011C16.9491 9.6792 17.5556 10.3017 18.5092 12.134C20.4916 15.9431 15.7388 17.0872 12.5748 17.0872C9.26797 17.0872 -0.640622 17.5 2.66617 17.5"
                :stroke="annotationColor"
                stroke-width="4"
                stroke-linecap="round"
              />
            </svg>
          </v-btn>
          <v-btn
            id="line-marker"
            color="editor-menu"
            elevation="3"
            icon
            :disabled="editorInteractionDisabled"
            :class="{ selected: isMarkerSelected(LineMarker.typeName) }"
            @click="lineMarker(true)"
          >
            <svg
              width="20"
              height="36"
              viewBox="0 0 20 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1="18.4549"
                y1="28.1453"
                x2="1.35642"
                y2="9.90687"
                :stroke="annotationColor"
                stroke-width="3"
              />
            </svg>
          </v-btn>
          <v-btn
            id="arrow-marker"
            color="editor-menu"
            elevation="3"
            icon
            :disabled="editorInteractionDisabled"
            :class="{ selected: isMarkerSelected(ArrowMarker.typeName) }"
            @click="arrowMarker(true)"
          >
            <v-icon>icon-arrow-top-left-large</v-icon>
          </v-btn>
          <v-btn
            id="text-marker"
            color="editor-menu"
            elevation="3"
            icon
            :disabled="editorInteractionDisabled"
            :class="{ selected: isMarkerSelected(TextMarker.typeName) }"
            @click="textMarker(true)"
          >
            <svg
              width="18"
              height="21"
              viewBox="0 0 18 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.9636 7.4L16.9636 7.775L13.5636 2.775H10.8136V18.925L13.4886 19.75L12.8636 21H4.76357V19.975L7.11357 18.125V2.775H4.43857L1.03857 7.775L0.0385743 7.4V0.899999L1.56357 1.35H16.4386L17.9636 0.899999V7.4Z"
                :fill="annotationColor"
              />
            </svg>
          </v-btn>
          <v-menu :location="'end'" :attach="true">
            <template #activator="{ props }">
              <v-btn
                id="shape-menu"
                color="editor-menu"
                elevation="3"
                icon
                :disabled="editorInteractionDisabled"
                v-bind="props"
                :class="{
                  selected:
                    isMarkerSelected(FrameMarker.typeName) ||
                    isMarkerSelected(EllipseMarker.typeName),
                }"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21V24C11.5817 24 8 20.4183 8 16C8 11.5817 11.5817 8 16 8C20.4183 8 24 11.5817 24 16C24 20.4183 20.4183 24 16 24V21C18.7614 21 21 18.7614 21 16Z"
                    :fill="annotationColor"
                  />
                  <path
                    d="M16 0V10L12 12L9 16H0V0H16ZM3 13H9.5L10.5 10.5L11.5 9.5L13 8.66927V3H3V13Z"
                    :fill="annotationColor"
                  />
                </svg>
              </v-btn>
            </template>
            <v-list>
              <v-list-item class="pa-0">
                <v-btn
                  id="rectangle-marker"
                  color="editor-menu"
                  icon
                  :disabled="editorInteractionDisabled"
                  :class="{ selected: isMarkerSelected(FrameMarker.typeName) }"
                  @click="rectangleMarker(true)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1.5"
                      y="1.5"
                      width="13"
                      height="13"
                      :stroke="annotationColor"
                      stroke-width="3"
                    />
                  </svg>
                </v-btn>
              </v-list-item>
              <v-list-item class="pa-0">
                <v-btn
                  id="ellipse-marker"
                  color="editor-menu"
                  icon
                  :disabled="editorInteractionDisabled"
                  :class="{
                    selected: isMarkerSelected(EllipseMarker.typeName),
                  }"
                  @click="ellipseMarker(true)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="6.5"
                      fill="#F3F5F9"
                      :stroke="annotationColor"
                      stroke-width="3"
                    />
                  </svg>
                </v-btn>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn
            id="dot-marker"
            color="editor-menu"
            elevation="3"
            icon
            class="dot-marker"
            :disabled="editorInteractionDisabled"
            :class="{
              selected: isMarkerSelected(DotMarker.typeName),
            }"
            @click="dot(true)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                :fill="annotationColor"
                d="M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z"
              />
              <path
                :fill="annotationColor"
                d="M23 12c0-6.075-4.925-11-11-11S1 5.925 1 12s4.925 11 11 11v1C5.373 24 0 18.627 0 12S5.373 0 12 0s12 5.373 12 12-5.373 12-12 12v-1c6.075 0 11-4.925 11-11Zm-7 0a4 4 0 1 0-8 0 4 4 0 0 0 8 0Zm2 0a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z"
              />
            </svg>
          </v-btn>

          <v-btn
            v-if="showRotate"
            id="rotate"
            color="editor-menu"
            elevation="3"
            icon
            class="rotate border-primary"
            :class="{
              'border-top': !isMobileLandscape,
              'border-side': isMobileLandscape,
            }"
            :disabled="resetDisabled || editorNotReady"
            @click="rotateImageBy90DegreesClockwise()"
          >
            <v-icon>icon-refresh</v-icon>
          </v-btn>

          <v-btn
            id="crop"
            color="editor-menu"
            elevation="3"
            icon
            class="crop"
            :disabled="resetDisabled || editorNotReady"
            @click="initializeCropper(true)"
          >
            <v-icon>icon-crop</v-icon>
          </v-btn>

          <v-btn
            id="remove-marker"
            color="editor-menu"
            elevation="3"
            icon
            class="trash"
            :disabled="editorInteractionDisabled"
            :class="{
              'rounded-b-circle': !isMobileLandscape,
              'rounded-te-circle': isMobileLandscape,
              'rounded-be-circle': isMobileLandscape,
              disabled: !canDelete,
            }"
            @click="deleteMarker"
          >
            <v-icon>icon-trash-full-stroke</v-icon>
          </v-btn>
        </div>
      </div>

      <div
        id="marker-container"
        ref="container"
        :aria-disabled="editorInteractionDisabled ? 'true' : undefined"
        :class="{
          'marker-interaction-disabled': editorInteractionDisabled,
        }"
        :inert="editorInteractionDisabled || undefined"
        @pointerdown.capture="trackMarkerPointer"
        @pointerup.capture="releaseMarkerPointer"
        @pointercancel.capture="releaseMarkerPointer"
      ></div>
    </div>
    <div ref="bottomControls" class="d-flex justify-center pb-4 bottom-container">
      <v-btn
        id="reset-image"
        color="editorMenu"
        rounded="pill"
        size="large"
        variant="flat"
        class="editor-cancel"
        :disabled="cropping ? resetDisabled : editorExitDisabled"
        @click="reset"
      >
        {{ cropping ? "Reset" : cancelLabel }}
      </v-btn>
      <v-btn
        id="save-image"
        color="primary"
        rounded="pill"
        size="large"
        variant="flat"
        class="ml-2"
        :disabled="saveDisabled"
        @click="save"
      >
        {{ cropping ? "Apply" : "Save" }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  shallowRef,
  onMounted,
  watch,
  computed,
  onUnmounted,
  reactive,
  nextTick,
  getCurrentInstance,
  onBeforeMount,
} from "vue";
import { useDisplay } from "vuetify";
import Cropper, { CropperImage } from "cropperjs";
import type { Selection } from "@cropper/element-selection";
import {
  type AnnotationState,
  ArrowMarker,
  FreehandMarker,
  LineMarker,
  TextMarker,
  FrameMarker,
  EllipseMarker,
  MarkerArea,
  Renderer,
  TextMarkerEditor,
  MarkerEditorEventData,
  FontSize,
  MarkerBase,
  LinearMarkerEditor,
  MarkerBaseEditor,
  ShapeMarkerEditor,
  IPoint,
  ISize,
  LinearMarkerBase,
  Activator,
} from "@markerjs/markerjs3";
import { useToast } from "vue-toastification";

import DotMarker from "./markers/DotMarker";
import LSTextMarker from "./markers/LSTextMarker";
import LSTextMarkerEditor from "./markers/LSTextMarkerEditor";
import {
  getUsableTextMarkerBottom,
  keepTextMarkerWithinTarget,
} from "./markers/keepTextMarkerWithinTarget";
import DotMarkerEditor from "./markers/DotMarkerEditor";
import ColorPicker from "./ColorPicker.vue";
import { rotateBlob } from "./rotateBlob";
import {
  openDiscardChangesDialog,
  UNSAVED_CHANGES_DIALOG_OPTIONS,
  UNSAVED_CHANGES_DIALOG_TEXT,
  type ConfirmDialogApi,
} from "../utilities/unsavedChangesDialog";

const toast = useToast();
const mobile = ref(useDisplay().smAndDown);

const props = defineProps<{
  targetImage: string | undefined;
  isEditing: boolean;
  conversationId: string;
  snapshotId?: string;
  snapshotSaved?: boolean;
  persistencePending?: boolean;
  targetHeight?: number;
  targetWidth?: number;
  defaultShowControls?: boolean;
  showRotate?: boolean;
  imageQuality?: number;
  cancelLabel?: string;
  isAsyncApp: boolean;
}>();

const emits = defineEmits<{
  (
    event: "imageSaved",
    image: Blob,
    clearEditor: boolean,
    persistenceAttemptId: string,
  ): void;
  (event: "discard", force: boolean): void;
  (event: "retake"): void;
  (event: "saveUnchanged"): void;
}>();

interface ConfirmDialogRoot {
  $confirm: ConfirmDialogApi;
}

function openEditorUnsavedChangesDialog(confirmDialog: ConfirmDialogApi) {
  return confirmDialog.open(
    UNSAVED_CHANGES_DIALOG_TEXT.title,
    UNSAVED_CHANGES_DIALOG_TEXT.message,
    false,
    UNSAVED_CHANGES_DIALOG_TEXT.agreeText,
    UNSAVED_CHANGES_DIALOG_TEXT.cancelText,
    false,
    false,
    UNSAVED_CHANGES_DIALOG_OPTIONS,
  );
}

const annotationImage = ref<HTMLImageElement>();
const container = ref<HTMLDivElement | null>(null);
// The full-screen editor overlay. Measured (not window.innerWidth/innerHeight) to derive the box the
// edited image is fitted into — see getEditorFitBox() for why that distinction matters (LSP-13917).
const editContainer = ref<HTMLDivElement | null>(null);
const bottomControls = ref<HTMLDivElement | null>(null);
const editor = ref<MarkerArea | null>(null);
const currentEditorTypeName = ref<string | null>();
const showControls = ref<boolean>(props.defaultShowControls ?? false);
const wasShowingControls = ref<boolean>(false);
const annotationColor = ref<string>("#000");
const orientation = ref<string>(resolveOrientation());
const cropping = ref<boolean>(false);
const isDirty = ref<boolean>(false);
const cropper = ref<Cropper>();
const cropSelectionChanged = ref<boolean>(false);
let clampingCropSelection = false;
const confirmingCropDiscard = ref<boolean>(false);
const cropContainer = ref<HTMLDivElement>();
const currentSelectedMarker = ref<MarkerBaseEditor | undefined>();
const currentState = ref<AnnotationState | null>();
let pendingMarkerDeleteCapture: MarkerArea | null = null;
let markerDeleteCaptureQueued = false;
let markerStateCaptureBlocked: MarkerArea | null = null;
const isSaving = ref<boolean>(false);
const currentImageUrl = ref<string | undefined>();
let savePersistenceAttemptId: string | undefined;

function getSavePersistenceAttemptId(): string {
  savePersistenceAttemptId ??= crypto.randomUUID();
  return savePersistenceAttemptId;
}
// Work deferred while a crop save (isSaving) or a Cancel/Reset confirm dialog (confirmingCropDiscard)
// is in flight, so it can run once that operation finishes instead of being silently dropped (orientation
// refresh) or tearing down/replacing the editor out from under the in-flight operation (targetImage/isEditing
// prop changes).
const pendingLayoutRefresh = ref<boolean>(false);
const pendingTargetImage = ref<string | undefined>();
const pendingEditingTeardown = ref<boolean>(false);
// Set once the annotation <img>'s decode completes, independent of props.isEditing at the time — the
// isEditing watcher below reads this to (re-)initialize the editor on a later false->true flip, since
// the onload handler that normally does that only fires once per image load and won't refire just
// because isEditing changed.
const annotationImageLoaded = ref<boolean>(false);
// Captured at the moment updateTargetImage() reassigns annotationImage.src, from props.isEditing at
// that instant — NOT from annotationImageLoaded (which updateTargetImage() itself always resets to
// false right before every reassignment, so it can never distinguish "this is a reload" from "this is
// the first load"). Lets the onload handler below tell a genuine stale-decode-outliving-session apart
// from the routine first mount-closed load.
const decodeStartedWhileEditing = ref<boolean>(false);
const confirmingEditorDiscard = ref<boolean>(false);
// Set synchronously (to the active editor instance) for the duration of a render() call from either
// initializeCropper() or rotateImageBy90DegreesClockwise() — both write the CURRENT editor.value in
// before their render() await and release it (only if they still own it) afterward. Scoping the lock
// to a specific editor instance, rather than a plain boolean, means a stale render left over from an
// already-discarded session can't block Save/Reset/Rotate/Crop on a freshly (re-)opened one: once
// editor.value changes, the lock no longer matches it and cropperInitializing reads false immediately,
// without waiting for the stale render's promise to ever settle.
const renderLockOwner = ref<MarkerArea | null>(null);
const cropperInitializing = computed(
  () =>
    renderLockOwner.value !== null && renderLockOwner.value === editor.value,
);
// MarkerJS may defer restoreState() until its private target-image clone finishes loading. Keep a
// per-editor lock through that whole interval; tying it to the instance makes clear()/teardown or a
// newer initialization release a stale restore without blocking the replacement editor.
const annotationRestoreOwner = ref<MarkerArea | null>(null);
const annotationRestorePending = computed(
  () =>
    annotationRestoreOwner.value !== null &&
    annotationRestoreOwner.value === editor.value,
);
// The in-flight operations a layout rebuild must never run underneath: updateTargetImage() ->
// clear(false) unconditionally resets isSaving, replaces the editor instance a Cancel/Reset confirm
// dialog or an in-flight crop-init/rotate render still holds a reference to, and restarts an
// annotation restore. Shared by runLayoutRefresh() and the isEditing reopen below so a reopen cannot
// apply a deferred refresh through a guard the scheduled path honours (break-it round 3).
const layoutRefreshBlocked = computed(
  () =>
    isSaving.value ||
    confirmingCropDiscard.value ||
    confirmingEditorDiscard.value ||
    cropperInitializing.value ||
    annotationRestorePending.value,
);

// MarkerJS has no failure event for a deferred restore. The private image usually settles from the
// browser cache because the outer annotation image already decoded, but keep a generous watchdog
// for missing DOM targets and browser event loss without treating a legitimately slow decode as a
// failure. Every success/failure/session replacement cancels this observer before it can fire.
const ANNOTATION_RESTORE_TIMEOUT_MS = 60_000;
let cancelAnnotationRestoreObservation: (() => void) | undefined;
function cancelPendingAnnotationRestoreObservation() {
  const cancelObservation = cancelAnnotationRestoreObservation;
  cancelAnnotationRestoreObservation = undefined;
  cancelObservation?.();
}
const editorInteractionDisabled = computed(
  () =>
    isSaving.value ||
    cropping.value ||
    cropperInitializing.value ||
    annotationRestorePending.value ||
    (props.persistencePending ?? false),
);
const activeMarkerPointers = new Map<number, EventTarget>();

function trackMarkerPointer(event: PointerEvent) {
  const pointerTarget = event.composedPath()[0];
  if (pointerTarget) {
    activeMarkerPointers.set(event.pointerId, pointerTarget);
  }
}

function releaseMarkerPointer(event: PointerEvent) {
  activeMarkerPointers.delete(event.pointerId);
}

function cancelActiveMarkerPointers() {
  for (const [pointerId, pointerTarget] of activeMarkerPointers) {
    const cancellationEvent =
      typeof PointerEvent === "undefined"
        ? new Event("pointercancel", { bubbles: true, composed: true })
        : new PointerEvent("pointercancel", {
            bubbles: true,
            composed: true,
            pointerId,
          });
    if (!("pointerId" in cancellationEvent)) {
      Object.defineProperty(cancellationEvent, "pointerId", {
        value: pointerId,
      });
    }
    pointerTarget.dispatchEvent(cancellationEvent);
    if (
      pointerTarget instanceof Element &&
      pointerTarget.hasPointerCapture?.(pointerId)
    ) {
      pointerTarget.releasePointerCapture(pointerId);
    }
  }
  activeMarkerPointers.clear();
}
// MarkerArea installs native pointer handlers on its shadow canvas and a keyup listener on window,
// so disabling only our Vue toolbar leaves a selected marker draggable/deletable while the parent
// is persisting an already-rendered blob. Cancel any pointer-captured drag, then deselect on every
// lock transition; the inert/pointer-events bindings on marker-container block new native gestures.
watch(editorInteractionDisabled, (interactionDisabled) => {
  if (interactionDisabled) {
    cancelActiveMarkerPointers();
    deselectMarker();
  }
});
// Bumped whenever crop mode is torn down so a pending initializeCropper() render() can tell that
// the crop it was building for got cancelled (e.g. a confirmed Cancel) while it was still in
// flight, even though editor.value/props.isEditing never changed (the broader editing session
// stayed open).
const cropSessionEpoch = ref<number>(0);
// Hands out a unique, increasing id to every updateTargetImage() call that reassigns the
// annotation image (the imageToDataUrl() conversion below it can be slow and network-driven).
const targetImageRequestId = ref<number>(0);
// ls-heroku-ci: the id above only tells a call how many OTHERS have STARTED since it began — not
// whether any of them actually SUCCEEDED. Comparing against it directly meant a newer overlapping
// call that later REJECTED (a bad URL, a network failure) would still cause an older call's
// perfectly good, already-resolved conversion to be discarded once it reached this check, even
// though nothing else ever ends up applying: annotationImage.src never gets set, its onload/
// onerror never fire, and annotationImageLoaded is stuck false with no visible sign anything
// failed. Tracking the highest id that actually reached the apply step (below) instead means a
// call is only discarded once a newer call has genuinely superseded it, not merely started.
const targetImageAppliedRequestId = ref<number>(0);
// Parent selections identify distinct media artifacts. Unlike an internal rotate/crop conversion,
// an older parent selection must never become the fallback merely because a newer selection fails:
// rendering photo A while the host now identifies photo B could upload A under B's artifact id.
const latestParentTargetImageRequestId = ref<number>(0);
// Session-ending clears bump this epoch. Confirm dialogs capture it so a response received after
// Retake, a target-image replacement, or another teardown cannot act on the new session. A narrow
// clear(false, false) is intentionally exempt while restoring the same live editing session.
const editingSessionEpoch = ref<number>(0);
interface ImageOperationSnapshot {
  imageUrl?: string;
  imageBlob?: Blob;
  structural?: boolean;
  state: AnnotationState | null | undefined;
  isDirty: boolean;
  cropSelectionChanged: boolean;
}
// Preserves the last image that was successfully editable while a replacement image is decoding.
// A browser-level decode failure happens after updateTargetImage() has intentionally cleared the
// editor, so this lets the error handler restore a usable editor instead of leaving the user in a
// permanently blank decode state.
const failedImageRecovery = ref<{
  requestId: number;
  appliedRequestIdAtStart: number;
  allowRecovery: boolean;
  imageUrl: string | undefined;
  imageBlob: Blob | undefined;
  state: AnnotationState | null | undefined;
  isDirty: boolean;
  cropSelectionChanged: boolean;
  imageOperationHistory: ImageOperationSnapshot[];
}>();
// This is intentionally independent from the per-request recovery above. Multiple remote
// conversions can overlap, and a later conversion must not erase the only known-good image while
// an earlier, already-applied image is still decoding.
// ls-heroku-ci (LSP-15699, round 3): imageOperationHistory must travel with this snapshot too, or
// restoreLastDecodedImage() restores currentImageUrl/currentState from here while leaving
// imageOperationHistory whatever it happened to be left at (already popped by the failed undo/op) —
// desyncing the undo stack from the actually-displayed image with no logging of the mismatch.
const lastDecodedImage = ref<{
  imageUrl: string;
  imageBlob: Blob | undefined;
  state: AnnotationState | null | undefined;
  isDirty: boolean;
  cropSelectionChanged: boolean;
  imageOperationHistory: ImageOperationSnapshot[];
}>();
// Marker edits share one Blob reference for their current raster. Crop/Rotate transitions retain at
// most five distinct raster Blobs, while the total cap also bounds large AnnotationState snapshots
// from long freehand-heavy sessions.
const MAX_STRUCTURAL_HISTORY_ENTRIES = 5;
const MAX_IMAGE_OPERATION_HISTORY_ENTRIES = 50;
const imageOperationHistory = shallowRef<ImageOperationSnapshot[]>([]);
const currentImageBlob = shallowRef<Blob>();
const activeImageObjectUrl = shallowRef<string>();
const pendingImageObjectUrlTransition = shallowRef<{
  previous?: string;
  next?: string;
}>();
// Cross-image Undo is asynchronous whether history retained a Blob or a plain URL. Keep its
// recovery ownership locked until the restored image (or its fallback) is actually usable.
const crossImageUndoPending = ref(false);

function beginImageObjectUrlTransition(next?: string) {
  const pendingTransition = pendingImageObjectUrlTransition.value;
  if (
    pendingTransition?.next &&
    pendingTransition.next !== pendingTransition.previous &&
    pendingTransition.next !== next
  ) {
    // A newer target-image request can supersede a Blob-backed Undo before that URL decodes. Revoke
    // the abandoned pending URL before replacing its transition record, or teardown can no longer
    // discover it and a rapid sequence of operations leaks every superseded raster.
    console.warn(
      "ImageEditor: superseding a pending image object URL transition",
      {
        conversationId: props.conversationId,
        supersededUrl: pendingTransition.next,
        replacementUrl: next,
      },
    );
    URL.revokeObjectURL(pendingTransition.next);
  }
  pendingImageObjectUrlTransition.value = {
    previous: activeImageObjectUrl.value,
    next,
  };
}

function commitImageObjectUrlTransition() {
  const transition = pendingImageObjectUrlTransition.value;
  if (!transition) return;
  if (transition.previous && transition.previous !== transition.next) {
    URL.revokeObjectURL(transition.previous);
  }
  activeImageObjectUrl.value = transition.next;
  pendingImageObjectUrlTransition.value = undefined;
}

function cancelImageObjectUrlTransition() {
  const transition = pendingImageObjectUrlTransition.value;
  if (!transition) return;
  if (transition.next && transition.next !== transition.previous) {
    URL.revokeObjectURL(transition.next);
  }
  pendingImageObjectUrlTransition.value = undefined;
}

function releaseImageObjectUrls() {
  const urls = new Set([
    activeImageObjectUrl.value,
    pendingImageObjectUrlTransition.value?.next,
    pendingImageObjectUrlTransition.value?.previous,
  ]);
  for (const url of urls) {
    if (url) URL.revokeObjectURL(url);
  }
  activeImageObjectUrl.value = undefined;
  pendingImageObjectUrlTransition.value = undefined;
  crossImageUndoPending.value = false;
  // Both values are session-owned, regardless of whether this session happened to expose its Blob
  // through an object URL. Clearing only the object-URL case retained an ordinary data-URL raster
  // (and its full Blob) after close/unmount, and made reopen behavior depend on image provenance.
  // A reopened session reloads props.targetImage through updateTargetImage's normal path below.
  currentImageUrl.value = undefined;
  currentImageBlob.value = undefined;
  failedImageRecovery.value = undefined;
}
function pushImageOperationHistory(snapshot: ImageOperationSnapshot) {
  let nextHistory = [...imageOperationHistory.value, snapshot];
  const structuralCount = nextHistory.filter(
    (entry) => entry.structural,
  ).length;
  if (structuralCount > MAX_STRUCTURAL_HISTORY_ENTRIES) {
    const oldestStructuralIndex = nextHistory.findIndex(
      (entry) => entry.structural,
    );
    nextHistory = nextHistory.slice(oldestStructuralIndex + 1);
  }
  if (nextHistory.length > MAX_IMAGE_OPERATION_HISTORY_ENTRIES) {
    // Marker states share the current raster, so evict the oldest one before discarding a
    // Blob-backed Crop/Rotate checkpoint that is still inside its stricter five-entry cap.
    const oldestMarkerStateIndex = nextHistory.findIndex(
      (entry) => !entry.structural,
    );
    // The stricter structural cap above guarantees a marker entry whenever total history exceeds
    // MAX_IMAGE_OPERATION_HISTORY_ENTRIES. Keep the index guard explicit so a future cap change
    // cannot turn findIndex(-1) into removal of the newest structural checkpoint.
    if (oldestMarkerStateIndex >= 0) {
      nextHistory.splice(oldestMarkerStateIndex, 1);
    }
  }
  imageOperationHistory.value = nextHistory;
}

function createImageOperationSnapshot(
  imageUrl: string,
  imageBlob: Blob | undefined,
  state: AnnotationState | null | undefined,
  dirty: boolean,
  selectionChanged: boolean,
  structural: boolean = false,
): ImageOperationSnapshot {
  return {
    imageUrl: imageBlob ? undefined : imageUrl,
    imageBlob,
    structural,
    state,
    isDirty: dirty,
    cropSelectionChanged: selectionChanged,
  };
}
const textWidthContext = ref<CanvasRenderingContext2D>();
const lastTextMarker = ref<TextMarker>();
const helperCanvas = ref<HTMLCanvasElement>();
// The isEditing watcher below runs with { immediate: true }, i.e. during setup, before onMounted has
// created helperCanvas or Vue has populated the annotationImage template ref. Its reopen branch must
// know whether it is running pre-mount so it can leave the initial load to onMounted. Deliberately a
// plain let, not a ref: nothing renders or computes from it, so reactivity would buy nothing. It is
// declared inside <script setup>, whose body runs per component instance, so each editor gets its
// own — a remounted editor correctly starts over at false.
let hasMounted = false;
const instance = getCurrentInstance();

function getConfirmDialogRoot(): ConfirmDialogRoot | null {
  return (
    (instance?.proxy?.$root as unknown as ConfirmDialogRoot | undefined) ?? null
  );
}

// Guarded editor operations share this logging shape, so a fail-condition can always be told apart
// from the others regardless of whether it occurred during save, crop initialization, a confirmation,
// a layout refresh, or a parent-driven operation.
function warnCropGuardSkipped(action: string) {
  const reasons: string[] = [];

  if (isSaving.value) {
    reasons.push("a save is in flight");
  }
  if (props.persistencePending) {
    reasons.push("parent persistence is in flight");
  }
  if (confirmingCropDiscard.value) {
    reasons.push("a crop discard confirm dialog is in flight");
  }
  if (confirmingEditorDiscard.value) {
    reasons.push("an editor discard confirmation is in flight");
  }
  if (cropperInitializing.value) {
    reasons.push("a crop initialization is in flight");
  }
  if (annotationRestorePending.value) {
    reasons.push("annotation restoration is in flight");
  }
  if (cropping.value) {
    reasons.push("a crop is in progress");
  }

  console.warn(
    `ImageEditor: ${action} skipped — ${reasons.join(", ") || "a crop guard is active"}`,
    {
      conversationId: props.conversationId,
      isSaving: isSaving.value,
      persistencePending: props.persistencePending ?? false,
      confirmingCropDiscard: confirmingCropDiscard.value,
      confirmingEditorDiscard: confirmingEditorDiscard.value,
      cropperInitializing: cropperInitializing.value,
      annotationRestorePending: annotationRestorePending.value,
      cropping: cropping.value,
    },
  );
}

defineExpose({
  hasUnsavedEditorWork,
  discard: discard,
  retake,
  editor,
});

// Develop's beforeunload predicate. Deliberately NOT the same as `hasChanges` below: this one also
// counts an un-applied crop selection, because closing the tab would lose it — whereas the Save
// button's gate only cares about work that Save would actually render (LSP-17747).
function hasUnsavedEditorWork(): boolean {
  return (
    (currentState.value?.markers?.length ?? 0) > 0 ||
    isDirty.value ||
    cropSelectionChanged.value
  );
}

function onBeforeUnload(event: BeforeUnloadEvent) {
  if (props.isEditing && hasUnsavedEditorWork()) {
    event.preventDefault();
    event.returnValue = "";
  }
}

let annotationImageDecodeSequence = 0;

function setAnnotationImageSource(
  imageUrl: string,
  targetImageRequestId: number,
) {
  const image = annotationImage.value;
  if (!image) {
    console.warn(
      "ImageEditor: image source update skipped — annotation image element is unavailable",
      {
        conversationId: props.conversationId,
        targetImageRequestId,
      },
    );
    return;
  }

  const decodeSequence = ++annotationImageDecodeSequence;
  const editingSessionAtDecodeStart = editingSessionEpoch.value;
  image.onload = async () => {
    if (decodeSequence !== annotationImageDecodeSequence) {
      console.warn(
        "ImageEditor: stale image load ignored — a newer decode owns the annotation image",
        {
          conversationId: props.conversationId,
          targetImageRequestId,
        },
      );
      return;
    }
    // The current <img> decoded successfully even if its editing session closed while the decode
    // was pending. Record readiness before the session guard so a later false->true reopen can
    // initialize from this already-loaded src (the browser will not emit load again for it).
    annotationImageLoaded.value = true;
    if (editingSessionAtDecodeStart !== editingSessionEpoch.value) {
      console.warn(
        "ImageEditor: a stale image decode completed after the editing session had already ended — skipping editor re-init",
        {
          conversationId: props.conversationId,
          isEditing: props.isEditing,
        },
      );
      return;
    }

    try {
      pendingLayoutRefresh.value = false;

      let imageInitializationFinalized = false;
      const finalizeDecodedImage = () => {
        if (imageInitializationFinalized) return;
        imageInitializationFinalized = true;
        if (currentImageUrl.value) {
          lastDecodedImage.value = {
            imageUrl: currentImageUrl.value,
            imageBlob: currentImageBlob.value,
            state: currentState.value,
            isDirty: isDirty.value,
            cropSelectionChanged: cropSelectionChanged.value,
            imageOperationHistory: imageOperationHistory.value.slice(),
          };
        }
        commitImageObjectUrlTransition();
        failedImageRecovery.value = undefined;
        crossImageUndoPending.value = false;
      };
      const recoverDeferredRestoreFailure = () => {
        if (failedImageRecovery.value?.allowRecovery) {
          if (!recoverOrReleaseFailedTargetImage(targetImageRequestId, true)) {
            crossImageUndoPending.value = false;
          }
          return;
        }
        // Parent-driven artifact swaps intentionally disallow old-image recovery. Their pending
        // transition/recovery record must still be released after the failed restore.
        cancelImageObjectUrlTransition();
        failedImageRecovery.value = undefined;
        crossImageUndoPending.value = false;
      };

      if (annotationImage.value && props.isEditing) {
        // updateTargetImage() already reset annotation state before assigning this source. This
        // decode-complete cleanup only replaces the transient editor instance; preserving dirty
        // state keeps a rotate/crop applied during the decode eligible for the outer Save.
        clear(false, false);
        const restored = initializeEditor("annotationImage.onload", true, {
          onSuccess: finalizeDecodedImage,
          onFailure: recoverDeferredRestoreFailure,
        });
        if (!restored) {
          recoverDeferredRestoreFailure();
          return;
        }
        setDimensions();
        if (annotationRestorePending.value) {
          // MarkerJS has only staged currentState so far. Keep failedImageRecovery and any Blob URL
          // transition live until arearestorestate proves the image is actually editable.
          console.warn(
            "ImageEditor: decoded image finalization deferred — annotation restoration is still in flight",
            {
              conversationId: props.conversationId,
              targetImageRequestId,
            },
          );
          return;
        }
      } else if (decodeStartedWhileEditing.value) {
        console.warn(
          "ImageEditor: a stale image decode completed after the editing session had already ended — skipping editor re-init",
          {
            conversationId: props.conversationId,
            isEditing: props.isEditing,
          },
        );
      }

      finalizeDecodedImage();
    } catch (err) {
      reportTargetImageFailure(err, "annotationImage.onload");
      // The raster decoded, but its editor did not become usable. Keep the object-URL transition
      // and recovery snapshot transactional just like a deferred restore failure: either rebuild
      // the previous known-good image or release the failed transition for a parent-owned swap.
      clear(false, false);
      if (failedImageRecovery.value?.allowRecovery) {
        if (!recoverOrReleaseFailedTargetImage(targetImageRequestId, true)) {
          crossImageUndoPending.value = false;
        }
      } else {
        cancelImageObjectUrlTransition();
        failedImageRecovery.value = undefined;
        crossImageUndoPending.value = false;
      }
    }
  };

  image.onerror = () => {
    if (
      decodeSequence !== annotationImageDecodeSequence ||
      editingSessionAtDecodeStart !== editingSessionEpoch.value
    ) {
      console.warn(
        "ImageEditor: stale image decode failure ignored — the request or editing session was superseded",
        {
          conversationId: props.conversationId,
          targetImageRequestId,
          isEditing: props.isEditing,
        },
      );
      return;
    }

    console.error(new Error("Annotation image failed to decode"), {
      tags: {
        component: "ImageEditor",
        method: "annotationImage.onerror",
        conversationId: props.conversationId,
      },
    });
    pendingLayoutRefresh.value = false;
    annotationImageLoaded.value = false;
    cancelImageObjectUrlTransition();

    let recoveryStarted = false;
    if (failedImageRecovery.value?.allowRecovery) {
      recoveryStarted = restoreFailedTargetImage(targetImageRequestId, true);
      if (!recoveryStarted) {
        recoveryStarted = restoreLastDecodedImage();
      }
    }
    if (!recoveryStarted) {
      crossImageUndoPending.value = false;
    }
    const message = "Failed to load image";
    const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
    toast.error(message, { timeout });
  };
  image.src = imageUrl;
}

onBeforeMount(() => {
  Activator.addKey("MJS3", import.meta.env.VITE_MARKERJS_KEY);
});

onMounted(async () => {
  hasMounted = true;
  // LSP-13917: these used to be mutually exclusive — wherever `screen.orientation` existed (every
  // current mobile browser, iPadOS Safari included) the resize listener was never attached, so
  // resizing the window (an iPad Split View / Stage Manager drag, a desktop window drag) never
  // refitted the photo at all. The editor kept whatever size the previous viewport produced until
  // some unrelated rebuild happened to run. Both are attached now and coalesced by
  // scheduleLayoutRefresh(), so a rotation firing both events still rebuilds exactly once. The
  // resize listener is registered FIRST and unconditionally, so a throwing/absent
  // `screen.orientation` can never cost us the resize half of the fix.
  window.addEventListener("resize", onWindowResize);
  try {
    const screenOrientation = screen.orientation;
    if (screenOrientation) {
      screenOrientation.addEventListener("change", onOrientationChange);
      // Captured rather than re-read at teardown: some WebViews replace the ScreenOrientation
      // object during navigation, and removing the handler from a different object than the one
      // that received it leaks the listener (break-it round 2).
      registeredScreenOrientation = screenOrientation;
    }
  } catch (err) {
    registeredScreenOrientation = undefined;
    console.warn(
      "ImageEditor: screen.orientation.addEventListener failed — resize listener remains registered",
      {
        conversationId: props.conversationId,
        error: err,
      },
    );
  }

  document.addEventListener("keydown", onKeyDown);
  window.addEventListener("beforeunload", onBeforeUnload);

  helperCanvas.value = document.createElement("canvas");

  // The initial image must use the same request sequencing as subsequent prop updates. Without
  // this, a slow remote conversion begun during mount could overwrite a newer prop value after it
  // had already been applied.
  // Async capture mounts this editor before a snapshot URL exists. That is an
  // expected empty state, not an image-load failure.
  if (props.targetImage) {
    try {
      await updateTargetImage(props.targetImage, true, false, true);
    } catch (err) {
      reportTargetImageFailure(err, "onMounted target image");
    }
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (context) {
    textWidthContext.value = context;
  }
});

onUnmounted(() => {
  hasMounted = false;
  // Invalidate callbacks that already captured this component's image/session before Vue clears
  // template refs. Listener cleanup cannot retract an onload/onerror function a browser or test
  // already queued, so both ownership tokens must move before session URLs are revoked.
  editingSessionEpoch.value++;
  annotationImageDecodeSequence++;
  annotationImageLoaded.value = false;
  cancelActiveMarkerPointers();
  cancelPendingAnnotationRestoreObservation();
  releaseImageObjectUrls();
  const screenOrientation = registeredScreenOrientation;
  registeredScreenOrientation = undefined;
  if (screenOrientation) {
    try {
      screenOrientation.removeEventListener("change", onOrientationChange);
    } catch (err) {
      console.warn(
        "ImageEditor: screen.orientation.removeEventListener failed, listener may leak",
        {
          conversationId: props.conversationId,
          error: err,
        },
      );
      // The resize listener and remaining teardown must still be cleaned up.
    }
  }
  window.removeEventListener("resize", onWindowResize);
  // A refresh queued by a viewport change moments before teardown would otherwise still fire its
  // frame/timeout and run updateTargetImage()/setDimensions() against a torn-down component
  // (LSP-13917).
  cancelScheduledLayoutRefreshAndResetWindow();

  document.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("beforeunload", onBeforeUnload);
});

const isMobileLandscape = computed(() => {
  const landscape = orientation.value.includes("landscape");
  return mobile.value && landscape;
});

const canDelete = computed(() => {
  return !!currentSelectedMarker.value && !currentEditorTypeName.value;
});

// Has the user actually edited the image — annotated, cropped or rotated it?
const hasChanges = computed(() => {
  return (currentState.value?.markers?.length ?? 0) > 0 || isDirty.value;
});

const saveDisabled = computed(() => {
  // cropperInitializing stays true for the whole initializeCropper() render() await, before
  // cropping.value itself flips true — without this check here too, Save could start its own
  // concurrent render() on the same editor/state while a Crop click's render() is still in flight.
  if (
    cropperInitializing.value ||
    annotationRestorePending.value ||
    props.persistencePending
  ) {
    return true;
  }
  if (confirmingEditorDiscard.value) {
    return true;
  }
  // saveCrop()'s reentrancy guard (isSaving) and confirmingCropDiscard both only matter while
  // cropping — this PR is what first made isSaving true during cropping at all (saveCrop() never
  // touched it before), so the pre-existing !cropping.value gate below never had to account for it.
  if (cropping.value) {
    return isSaving.value || confirmingCropDiscard.value;
  }
  // ls-heroku-ci: a rotate's own updateTargetImage() nulls editor.value synchronously and only
  // resolves once the re-decode's onload fires, but isDirty is set true (making hasChanges true)
  // right after that call returns — well before the decode, and re-init, complete. Without this,
  // Save renders enabled during that gap. save() now diagnoses its null-editor early return, but
  // disabling the control avoids a no-op action while the replacement image is still decoding.
  //
  // LSP-16034 / LSP-17747 gave contact-admin a trailing change-gate here, because Admin Save uploads
  // a NEW annotated copy and with nothing annotated there is genuinely nothing to save (LSP-13697),
  // while the async capture flow — where the photo ITSELF is what Save commits — stayed clickable
  // with nothing changed. LSP-20011 drops that asymmetry: per epic LSP-16731 no primary action is
  // disabled for "nothing changed", in EITHER app. The duplicate-copy protection it existed for is
  // unchanged and now enforced in one place — save()'s `saveUnchanged` emit, which hands the
  // decision to the parent instead of re-rasterising and re-uploading an untouched image.
  // `isSaving` (in-flight) and `editorNotReady` stay: a rotate's updateTargetImage() nulls
  // editor.value synchronously and only resolves when the re-decode's onload fires, while isDirty
  // flips true right after that call returns — Save would otherwise render enabled during that gap
  // and act on a half-replaced editor.
  return isSaving.value || editorNotReady.value;
});

// Mirrors the stateful Crop Reset guards. Terminal Cancel/Close uses editorExitDisabled below so a
// missing MarkerJS completion cannot trap the user, while Crop Reset must keep waiting because it
// reads and mutates the active crop/editor state. cropperInitializing is unconditional because
// cropping.value itself does not flip true until initializeCropper()'s render await completes.
const resetDisabled = computed(
  () =>
    isSaving.value ||
    (props.persistencePending ?? false) ||
    cropperInitializing.value ||
    annotationRestorePending.value ||
    confirmingEditorDiscard.value ||
    (cropping.value && confirmingCropDiscard.value),
);

// Annotation restoration locks every operation that reads or mutates MarkerJS state, but it must
// not trap the user in the editor until the watchdog fires. Cancel/Close owns terminal teardown and
// can safely invalidate the restoring instance; Crop Reset still uses resetDisabled because it
// operates on the live crop selection instead of exiting the session.
const editorExitDisabled = computed(
  () =>
    isSaving.value ||
    (props.persistencePending ?? false) ||
    cropperInitializing.value ||
    confirmingEditorDiscard.value ||
    (cropping.value && confirmingCropDiscard.value),
);

// ls-heroku-ci (break-it round 1, xhigh): Rotate and Crop both need editor.value to do their work
// (rotate()/initializeCropper() both bail out and warn when it's null — see their own guards) —
// unlike Reset/Cancel, which never touch editor.value at all. Folding this into resetDisabled itself
// would incorrectly disable Reset/Cancel too during the same window, so it's kept as its own
// computed and added only to the two buttons that actually need it.
const editorNotReady = computed(() => !editor.value);

// ls-heroku-ci (LSP-15699, finding #4): must mirror every guard undo() itself checks — otherwise
// the button renders enabled (via the `disabled: !canUndo` class binding) while a click silently
// no-ops through warnCropGuardSkipped(), with no user-visible feedback that anything was blocked.
// Deliberately does NOT check annotationImageLoaded.value bluntly — an Undo fired immediately after
// a Crop/Rotate Apply (whose own forward decode is still pending) is safe and expected to work:
// undo() discards that pending image and restores an already-known-good prior one. Only the
// isSameImage-specific case below (Cursor Bugbot, round 3) needs the decode flag: mirrors undo()'s
// own isSameImage && !annotationImageLoaded.value bail, so the button doesn't stay visually enabled
// while a click would silently hit that same early return.
const undoActionBlocked = computed(
  () =>
    isSaving.value ||
    (props.persistencePending ?? false) ||
    cropping.value ||
    cropperInitializing.value ||
    annotationRestorePending.value ||
    confirmingEditorDiscard.value ||
    confirmingCropDiscard.value,
);
const canUndo = computed(() => {
  if (
    imageOperationHistory.value.length === 0 ||
    undoActionBlocked.value ||
    crossImageUndoPending.value
  ) {
    return false;
  }

  const topSnapshot = imageOperationHistory.value.at(-1);
  if (
    (topSnapshot?.imageBlob
      ? topSnapshot.imageBlob === currentImageBlob.value
      : topSnapshot?.imageUrl === currentImageUrl.value) &&
    !annotationImageLoaded.value
  ) {
    return false;
  }

  return true;
});

const cancelLabel = computed(() => props.cancelLabel ?? "Cancel");

// Device detection utilities (inline to avoid Bowser dependency in the shared package)

function isIOSDevice(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function getFallbackOrientation(): string {
  try {
    const isPortrait = window.innerHeight > window.innerWidth;
    return isPortrait ? "portrait" : "landscape";
  } catch (err) {
    console.warn(
      "ImageEditor: window dimensions read failed — using portrait fallback",
      {
        conversationId: props.conversationId,
        error: err,
      },
    );
    return "portrait";
  }
}

function resolveOrientation(): string {
  let screenOrientation: unknown;
  // A hostile/instrumented shim can expose a throwing orientation getter; handlers must never throw.
  try {
    screenOrientation = screen.orientation?.type;
  } catch (err) {
    console.warn(
      "ImageEditor: screen.orientation.type read failed — using window dimensions",
      {
        conversationId: props.conversationId,
        error: err,
      },
    );
    return getFallbackOrientation();
  }

  const normalizedOrientation =
    typeof screenOrientation === "string" ? screenOrientation.trim() : "";
  if (normalizedOrientation) {
    return normalizedOrientation;
  }
  if (screenOrientation !== undefined && screenOrientation !== null) {
    console.warn(
      "ImageEditor: unusable screen.orientation.type discarded — using window dimensions",
      {
        conversationId: props.conversationId,
        screenOrientation,
      },
    );
  }
  return getFallbackOrientation();
}

// Which viewport event armed a scheduled refresh. A rotation outranks a queued resize, so the
// scheduler has to know what it is already holding, not just that it is holding something.
type LayoutRefreshSource = "onOrientationChange" | "onWindowResize";

// Goes against our dev standards but iOS needs this short time for things to settle during an
// orientation change. Otherwise, the image dimensions are calculated incorrectly because the screen
// dimensions that come back are still from the previous orientation.
const LAYOUT_REFRESH_SETTLE_MS = 200;
// Ceiling on how long the trailing settle window above may keep postponing a refresh. A slow drag of
// an iPad Split View divider emits resize events continuously for seconds; without this cap every
// event would push the rebuild further out and the photo would stay visibly mis-fitted for the whole
// drag (break-it round 2).
const MAX_LAYOUT_REFRESH_DEFERRAL_MS = 250;
// The elapsed cap above is blind to a clock that never advances (a frozen fake timer, an
// instrumented Date.now, a clock that steps backwards). Bound the number of postponements too, so a
// continuous event stream cannot starve the rebuild whatever the clock reports.
const MAX_LAYOUT_REFRESH_POSTPONEMENTS = 5;

let scheduledLayoutRefreshFrame: number | undefined;
let scheduledLayoutRefreshTimeout: ReturnType<typeof setTimeout> | undefined;
let layoutRefreshRequestedAt: number | undefined;
let scheduledLayoutRefreshSource: LayoutRefreshSource | undefined;
let consecutiveLayoutRefreshPostponements = 0;
let pendingLayoutRefreshSkipLogged = false;
// The editing session that armed the currently scheduled refresh, so a rebuild cannot land on a
// session that has since been torn down (break-it round 4).
let layoutRefreshSessionEpoch: number | undefined;
// The ScreenOrientation object the change handler was actually registered on. Captured rather than
// re-read at teardown: some WebViews replace it during navigation, and removing the handler from a
// different object than the one that received it leaks the listener (break-it round 2).
let registeredScreenOrientation: ScreenOrientation | undefined;
// See runLayoutRefresh's no-image guard: one warning per image-less stretch, cleared once an image
// is loaded again.
let imagelessLayoutRefreshWarningLatched = false;

function clearScheduledLayoutRefreshTimers() {
  if (scheduledLayoutRefreshFrame !== undefined) {
    cancelAnimationFrame(scheduledLayoutRefreshFrame);
    scheduledLayoutRefreshFrame = undefined;
  }
  if (scheduledLayoutRefreshTimeout !== undefined) {
    clearTimeout(scheduledLayoutRefreshTimeout);
    scheduledLayoutRefreshTimeout = undefined;
  }
}

function resetScheduledLayoutRefreshWindow() {
  layoutRefreshRequestedAt = undefined;
  scheduledLayoutRefreshSource = undefined;
  consecutiveLayoutRefreshPostponements = 0;
  pendingLayoutRefreshSkipLogged = false;
  layoutRefreshSessionEpoch = undefined;
}

function cancelScheduledLayoutRefreshAndResetWindow() {
  clearScheduledLayoutRefreshTimers();
  resetScheduledLayoutRefreshWindow();
}

/**
 * Drops a viewport refresh queued or scheduled by an earlier viewport change, both halves together.
 *
 * The deferred flag and the not-yet-fired settle run are two independent records of the SAME pending
 * rebuild, so anything that satisfies or abandons one must do the other — clearing only the flag
 * leaves an armed run that replaces a freshly built MarkerArea moments later (break-it round 2).
 */
function discardQueuedLayoutRefresh() {
  pendingLayoutRefresh.value = false;
  cancelScheduledLayoutRefreshAndResetWindow();
}

/**
 * Reads the wall clock without letting a hostile/instrumented Date.now throw out of an event handler.
 * @returns The current epoch milliseconds, or undefined if the clock is unreadable or non-finite
 */
function readLayoutRefreshClock(source: LayoutRefreshSource): number | undefined {
  try {
    const currentTime = Date.now();
    return Number.isFinite(currentTime) ? currentTime : undefined;
  } catch (err) {
    console.warn(
      "ImageEditor: Date.now failed while scheduling layout refresh — using postponement bound",
      {
        conversationId: props.conversationId,
        source,
        error: err,
      },
    );
    return undefined;
  }
}

/**
 * Queues one settled layout refresh for a viewport change.
 *
 * LSP-13917: a rotation fires BOTH `orientationchange` and `resize`, and a slow drag of an iPad
 * Split View divider fires a burst of `resize` events. Coalescing them through a single cancellable
 * frame/timeout pair means the rebuild runs once, against the box the viewport actually settled on,
 * instead of once per event against half-finished intermediate sizes.
 * @param source The viewport event requesting this refresh
 */
function scheduleLayoutRefresh(source: LayoutRefreshSource) {
  orientation.value = resolveOrientation();

  const requestedAt = readLayoutRefreshClock(source);
  const settleRunIsArmed =
    scheduledLayoutRefreshFrame !== undefined ||
    scheduledLayoutRefreshTimeout !== undefined;
  // A rotation outranks a queued resize: the caps exist to bound a resize stream, and a rotation is
  // the event this ticket is actually about, so let it re-arm the settle window the caps would
  // otherwise have frozen. A rotation cannot outrank another rotation, so orientation streams stay
  // bounded (LSP-13917).
  const orientationUpgradesPendingResize =
    settleRunIsArmed &&
    source === "onOrientationChange" &&
    scheduledLayoutRefreshSource === "onWindowResize";
  if (orientationUpgradesPendingResize) {
    resetScheduledLayoutRefreshWindow();
  }

  if (layoutRefreshRequestedAt === undefined) {
    layoutRefreshRequestedAt = requestedAt;
  }
  const waitedSoFar =
    requestedAt !== undefined && layoutRefreshRequestedAt !== undefined
      ? requestedAt - layoutRefreshRequestedAt
      : undefined;
  // An unbroken stream of viewport events must not starve the rebuild indefinitely: once the first
  // request in this burst has waited out the cap, stop pushing the already-armed settle run further
  // out and let it fire. Deliberately NOT an immediate synchronous rebuild — the armed run measures
  // the box at fire time, so letting it stand keeps the newest measurements and cannot double up with
  // a run this call would otherwise have started itself (break-it round 3).
  //
  // A backwards jump (an NTP correction) is deliberately NOT reported as a cap hit: it is not
  // evidence the burst has been deferred too long, and the postponement counter below already makes
  // a moving — or frozen — clock unable to postpone the rebuild forever.
  const elapsedDeferralLimitReached =
    settleRunIsArmed &&
    !orientationUpgradesPendingResize &&
    waitedSoFar !== undefined &&
    waitedSoFar >= MAX_LAYOUT_REFRESH_DEFERRAL_MS;
  // The elapsed cap above is blind to an unreadable or frozen clock, so count postponements too.
  const postponementLimitReached =
    settleRunIsArmed &&
    !orientationUpgradesPendingResize &&
    consecutiveLayoutRefreshPostponements >= MAX_LAYOUT_REFRESH_POSTPONEMENTS;
  // A resize arriving behind a queued rotation has nothing to add: the rotation's run measures the
  // settled box anyway, and re-arming here would push the rotation's own rebuild further out.
  const orientationRefreshIsPending =
    settleRunIsArmed &&
    source === "onWindowResize" &&
    scheduledLayoutRefreshSource === "onOrientationChange";

  if (
    elapsedDeferralLimitReached ||
    postponementLimitReached ||
    orientationRefreshIsPending
  ) {
    // Logged once per burst: a resize stream would otherwise emit one warning per event.
    if (!pendingLayoutRefreshSkipLogged) {
      const branch = elapsedDeferralLimitReached
        ? "elapsedDeferralLimitReached"
        : postponementLimitReached
          ? "postponementLimitReached"
          : "orientationRefreshIsPending && source === onWindowResize";
      console.warn(`ImageEditor: scheduleLayoutRefresh skipped — ${branch}`, {
        conversationId: props.conversationId,
        source,
        scheduledLayoutRefreshSource,
        waitedSoFar,
        consecutiveLayoutRefreshPostponements,
      });
      pendingLayoutRefreshSkipLogged = true;
    }
    return;
  }

  if (settleRunIsArmed && !orientationUpgradesPendingResize) {
    consecutiveLayoutRefreshPostponements++;
  }
  scheduledLayoutRefreshSource = source;
  clearScheduledLayoutRefreshTimers();
  // Stamped ONLY when an editor actually exists to be torn down. The guard in runLayoutRefresh
  // exists to stop a refresh armed before Save/Retake/Discard from rebuilding after it, so it needs
  // a session to protect. With no editor there is no session, and stamping anyway silently dropped
  // a legitimate refresh: a resize arriving during onMounted arms before the awaited initial
  // updateTargetImage(), whose clear() bumps editingSessionEpoch — so the epoch the refresh captured
  // never matched again and the mount-time resize was lost.
  layoutRefreshSessionEpoch = editor.value ? editingSessionEpoch.value : undefined;
  scheduledLayoutRefreshFrame = requestAnimationFrame(() => {
    scheduledLayoutRefreshFrame = undefined;
    scheduledLayoutRefreshTimeout = setTimeout(() => {
      scheduledLayoutRefreshTimeout = undefined;
      const method = `${source} layout refresh`;
      // The window (and its epoch) belongs to the run that is now firing; clear the burst
      // bookkeeping but keep the epoch until runLayoutRefresh has checked it.
      const armedSessionEpoch = layoutRefreshSessionEpoch;
      resetScheduledLayoutRefreshWindow();
      layoutRefreshSessionEpoch = armedSessionEpoch;
      void runLayoutRefresh(method);
    }, LAYOUT_REFRESH_SETTLE_MS);
  });
}

async function runLayoutRefresh(method: string) {
  // Save, Retake and Discard all end the editing session (each bumps editingSessionEpoch via
  // clear()), and a rotation moments before any of them leaves a settle run armed. Rebuilding here
  // would resurrect an empty MarkerArea over an editor the user already closed. Deliberately keyed
  // on the session epoch rather than props.isEditing: a refresh requested while editing is merely
  // closed — but not torn down — must still defer via pendingLayoutRefresh so the next reopen is
  // correctly sized (break-it round 4).
  if (
    layoutRefreshSessionEpoch !== undefined &&
    layoutRefreshSessionEpoch !== editingSessionEpoch.value
  ) {
    console.warn(
      `ImageEditor: ${method} skipped — the editing session that requested it has ended`,
      {
        conversationId: props.conversationId,
        isEditing: props.isEditing,
      },
    );
    layoutRefreshSessionEpoch = undefined;
    return;
  }
  layoutRefreshSessionEpoch = undefined;
  // Nothing left to re-fit. Every terminal teardown (reset/discard/retake/editing-closed) releases
  // currentImageUrl, and updateTargetImage(undefined, false) takes its state-preserving branch and
  // builds a fresh, EMPTY MarkerArea — resurrecting an editor over a session the user already left.
  // The session-epoch guard above cannot catch this: a viewport event arriving AFTER the teardown
  // arms its refresh under the NEW epoch, which matches.
  //
  // Checked BEFORE every deferring guard below, not after: the same teardown also clears
  // annotationImageLoaded, so the still-decoding guard would otherwise claim this refresh for a
  // decode that is never coming and leave pendingLayoutRefresh armed for the next reopened session
  // (break-it round 1).
  if (!currentImageUrl.value) {
    // Latched per image-less stretch, not throttled by time: contact-async keeps this component
    // mounted with no image for the WHOLE capture session (DisconnectedHomePage), so every settled
    // resize reaches here and an unlatched warning would bury the genuinely diagnostic logs
    // (including the closed-editor one below) in steady-state noise. The latch clears the moment an
    // image is loaded again, so the next image-less stretch reports once more.
    if (!imagelessLayoutRefreshWarningLatched) {
      imagelessLayoutRefreshWarningLatched = true;
      console.warn(
        `ImageEditor: ${method} skipped — no image is loaded to re-fit`,
        {
          conversationId: props.conversationId,
          isEditing: props.isEditing,
        },
      );
    }
    discardQueuedLayoutRefresh();
    return;
  }
  imagelessLayoutRefreshWarningLatched = false;
  // updateTargetImage(_, false) -> clear(false) unconditionally resets isSaving, and (while
  // cropping) initializeEditor() rebuilds the cropper via clearCropper(false) + a fresh
  // initializeCropper() — either would undermine an in-flight Apply's isSaving guard or
  // replace the cropper out from under a pending Cancel/Reset confirm dialog. Defer the
  // refresh while either is in flight instead of dropping it — the pendingLayoutRefresh flush
  // below re-runs it once both guards clear, so a rotate mid-Apply still ends up correctly sized.
  // cropperInitializing is checked too: rotateImageBy90DegreesClockwise() holds the render lock
  // (making this computed true) for its whole render/rotate/decode chain WITHOUT ever setting
  // isSaving — clear(false) nulling editor.value here would replace the editor Rotate is still
  // holding a reference to, so its own post-await identity check would then silently discard
  // the completed rotation as if the session had ended, even though nothing else did.
  // layoutRefreshBlocked is exactly this guard list (isSaving, confirmingCropDiscard,
  // confirmingEditorDiscard, cropperInitializing, annotationRestorePending) hoisted into a computed
  // so the scheduled path, the flush watcher and the isEditing reopen all honour the same gates.
  if (layoutRefreshBlocked.value) {
    warnCropGuardSkipped(method);
    pendingLayoutRefresh.value = true;
    return;
  }
  // A saveCrop()/rotate()-triggered decode can still be in flight even once isSaving has
  // cleared (clear(), called from inside updateTargetImage(), resets isSaving synchronously
  // well before the reassigned image finishes decoding) — measuring against a not-yet-decoded
  // image here would read 0/NaN. Defer the same way; the annotationImage onload handler
  // flushes this once the decode genuinely finishes.
  if (!annotationImageLoaded.value) {
    console.warn(
      `ImageEditor: ${method} deferred — annotation image is still decoding`,
      {
        conversationId: props.conversationId,
        isEditing: props.isEditing,
      },
    );
    pendingLayoutRefresh.value = true;
    return;
  }
  // The overlay is hidden behind `v-show="isEditing"`, and contact-async keeps this component
  // mounted for the whole capture session (DisconnectedHomePage), so an ordinary window resize
  // reaches this while nobody is editing. Rebuilding MarkerArea for a surface no one can see is pure
  // churn, so defer instead — the isEditing reopen below applies it, which also keeps the reopened
  // editor fitted to the box the viewport actually settled on.
  if (!props.isEditing) {
    // The only fail-condition in this gate that used to return silently. Every sibling above logs,
    // and CLAUDE.md -> "Diagnostic logging when fixing silent failures" requires every condition in
    // the gate to log, not just the one that caused the incident — this PR exists precisely because
    // a silent refit failure took hours to reconstruct.
    console.warn(
      `ImageEditor: ${method} deferred — the editor is closed, so there is no visible surface to re-fit`,
      {
        conversationId: props.conversationId,
        isEditing: props.isEditing,
      },
    );
    pendingLayoutRefresh.value = true;
    return;
  }
  // This run IS the refresh any earlier guarded attempt queued, so consume the flag rather than
  // leaving the flush watcher to rebuild a second time on the next guard transition (break-it
  // round 3).
  await applyLayoutRefresh(method);
}

/**
 * Rebuilds the editor against the current fit box and consumes the pending-refresh flag.
 *
 * Shared by the scheduled run, the save/confirmation flush watcher and the isEditing reopen so all
 * three refit the same way and none can leave the flag (or a still-armed settle run) behind.
 * @param method The label used for this refresh's failure diagnostics
 */
async function applyLayoutRefresh(method: string) {
  // A settle run armed by a later viewport event would otherwise rebuild a second time straight
  // after this one (break-it round 3).
  discardQueuedLayoutRefresh();
  try {
    await updateTargetImage(currentImageUrl.value, false);
    setDimensions();
  } catch (err) {
    reportTargetImageFailure(err, method);
  }
}

function onOrientationChange() {
  scheduleLayoutRefresh("onOrientationChange");
}

function onWindowResize() {
  scheduleLayoutRefresh("onWindowResize");
}

function onKeyDown(evt: KeyboardEvent) {
  // ls-heroku-ci (LSP-15699, round 3): entering Crop mode doesn't deselect/detach the live
  // MarkerArea — it only rasterizes a separate bitmap for the Cropper instance (initializeCropper()
  // renders markers into `src` while editor.value stays live underneath). Without this guard, a
  // Delete keypress here deletes the marker from the live editor (updating currentState/history)
  // AFTER the crop's source bitmap was already captured with that marker baked into its pixels —
  // Apply then produces a crop that still shows the "deleted" marker while state/history say it's
  // gone, and Undo later restores an image without a marker that's actually still visible. Same
  // reasoning as why the Undo control itself is excluded while cropping (its toolbar is covered by
  // the crop overlay) — this keyboard shortcut must not bypass that.
  if (evt.key !== "Delete" || !currentSelectedMarker.value || !editor.value) {
    return;
  }
  if (editorInteractionDisabled.value) {
    warnCropGuardSkipped("delete marker action");
    return;
  }
  editor.value.deleteSelectedMarkers();
}

function deleteMarker() {
  if (editorInteractionDisabled.value) {
    warnCropGuardSkipped("delete marker action");
    currentEditorTypeName.value = null;
    currentSelectedMarker.value = undefined;
    return;
  }
  if (editor.value && currentSelectedMarker.value) {
    editor.value.deleteSelectedMarkers();
    currentSelectedMarker.value = undefined;
  }
}

function markerEventBlocked(sourceEditor: MarkerArea, eventName: string) {
  if (sourceEditor !== editor.value) {
    console.warn(`ImageEditor: ignoring stale ${eventName} event`, {
      conversationId: props.conversationId,
    });
    return true;
  }

  if (!editorInteractionDisabled.value) {
    return false;
  }

  warnCropGuardSkipped(`${eventName} marker event`);
  currentEditorTypeName.value = null;
  currentSelectedMarker.value = undefined;
  return true;
}

function markerActionBlocked(action: string): boolean {
  if (!editorInteractionDisabled.value) {
    return false;
  }
  warnCropGuardSkipped(`${action} marker action`);
  currentEditorTypeName.value = null;
  currentSelectedMarker.value = undefined;
  return true;
}

function failClosedAnnotationStateRestore(
  sourceEditor: MarkerArea,
  stateBeingRestored: AnnotationState,
  method: string,
  error: Error,
): boolean {
  // A detached MarkerArea can finish loading its private target-image clone after a resize, Undo,
  // or teardown has already replaced it. Its missing completion signal says nothing about the new
  // editor's state, so never let that stale callback clear the live session.
  if (
    sourceEditor !== editor.value ||
    currentState.value !== stateBeingRestored
  ) {
    if (annotationRestoreOwner.value === sourceEditor) {
      annotationRestoreOwner.value = null;
    }
    console.warn(
      "ImageEditor: ignoring stale deferred annotation restore failure",
      {
        conversationId: props.conversationId,
        method,
      },
    );
    return false;
  }

  // The editor is empty if MarkerJS failed while rebuilding its markers. Detach every local dirty
  // signal from that empty raster so Save cannot overwrite an annotated image with a blank copy.
  currentState.value = null;
  isDirty.value = false;
  cropSelectionChanged.value = false;
  currentSelectedMarker.value = undefined;
  currentEditorTypeName.value = null;
  // A restore can fail synchronously before the orientation rebuild reaches clearCropper(false),
  // or asynchronously after it already removed the old instance. End either owned crop session:
  // retaining the old Cropper would leave Apply enabled against stale raster/marker state, while
  // retaining an orphaned mode would leave Reset/Cancel visible over no usable crop surface.
  if (cropping.value) {
    clearCropper();
  }
  if (annotationRestoreOwner.value === sourceEditor) {
    annotationRestoreOwner.value = null;
  }
  // MarkerJS still owns the private image callback that deferred this restore. Removing our
  // observers cannot cancel it, so detach the failed instance before that callback can later paint
  // rejected annotations back into the live editor after this fail-closed boundary.
  container.value?.replaceChildren();
  if (editor.value === sourceEditor) {
    editor.value = null;
  }
  reportTargetImageFailure(error, method);
  return true;
}

interface AnnotationRestoreCallbacks {
  onSuccess?: () => void;
  onFailure?: () => void;
}

function initializeEditor(
  restoreFailureMethod: string = "initializeEditor restoreState",
  reportUnavailable: boolean = true,
  restoreCallbacks?: AnnotationRestoreCallbacks,
): boolean {
  if (container.value) {
    editor.value = reactive(new MarkerArea());
    const editorAtInitialization = editor.value;

    const imgStyle = document.createElement("style");

    // iOS/Safari does not handle centering the image in the editor nicely so we have to calculate
    // the rendered image size and manually set the coordinates. Both the offset and the image size
    // must come from the SAME measured box, or the image is centred against a viewport it was never
    // sized for (LSP-13917).
    const editorFitBox = getEditorFitBox();
    const [fittedWidth, fittedHeight] = annotationImage.value
      ? calculateImageDimensions(
          annotationImage.value.naturalWidth,
          annotationImage.value.naturalHeight,
          editorFitBox,
        )
      : [0, 0];

    // An unusable fitted size (an image that has not decoded, an unmeasurable overlay) falls through
    // to the flex centring below rather than offsetting the image by half of an empty box.
    if (
      isIOSDevice() &&
      editorFitBox &&
      isUsableSize(fittedWidth) &&
      isUsableSize(fittedHeight)
    ) {
      const centeringOffset = calculateEditorCenteringOffset(
        editorFitBox,
        fittedWidth,
        fittedHeight,
      );
      // Center on BOTH axes. iOS/Safari doesn't center the editor image reliably with flex, so we
      // position it absolutely. Keying the centered axis on device orientation alone (top for
      // portrait, left for landscape) left the image left-aligned whenever the fitted image was
      // narrower than the screen while the device was in portrait — e.g. a tall photo on iPad
      // Safari, which fits to height and ends up narrower than the (relatively square) iPad
      // viewport (LSP-18569). Both offsets are emitted, and both come from the MEASURED fit box
      // rather than window.innerWidth/innerHeight, which are pinch-shrunken on iOS and are the
      // original LSP-13917 defect. Clamped to >= 0 inside calculateEditorCenteringOffset so an
      // image larger than the box is never pushed off-screen. The <img> and its sibling <svg>
      // marker overlay receive identical offsets, so they shift together and the annotation
      // coordinate mapping is unchanged.
      const position = `top: ${centeringOffset.top}px; left: ${centeringOffset.left}px`;

      imgStyle.innerHTML = `
							.canvas-container img { position: absolute; ${position}; }
							.canvas-container svg { margin: 0 !important; position: absolute; ${position}; }
						`;
    } else {
      imgStyle.innerHTML = `
							.canvas-container img { display: flex; justify-self: center; align-self: center; }
							.canvas-container svg { margin: 0 !important; display: flex; justify-self: center; align-self: center; }
						`;
    }

    const canvasStyle = document.createElement("style");
    // LSP-13917 (Michal Kieloch): MarkerJS sets `touch-action: pinch-zoom` INLINE on its canvas
    // container, which lets a pinch inside the editor zoom the browser's visual viewport. On iOS
    // that shrinks window.innerWidth/innerHeight, and any layout rebuild during the zoom (a
    // rotation) used to bake those shrunken values into the image size. getEditorFitBox() no longer
    // reads them, and this rule removes the gesture itself so the editor surface only ever sees
    // marker drawing/dragging. `!important` is required to beat MarkerJS's inline style.
    canvasStyle.innerHTML =
      ".canvas-container { place-items: unset !important; overflow: unset !important; touch-action: none !important; } .pinch-zoom-container { height: 100% !important; width: 100% }";
    editor.value.shadowRoot?.appendChild(imgStyle);
    editor.value.shadowRoot?.appendChild(canvasStyle);
    editor.value.registerMarkerType(
      DotMarker,
      DotMarkerEditor as unknown as typeof MarkerBaseEditor<MarkerBase>,
    );
    editor.value.registerMarkerType(
      LSTextMarker,
      LSTextMarkerEditor as unknown as typeof MarkerBaseEditor<MarkerBase>,
    );
    editor.value.targetImage = annotationImage.value;
    container.value.appendChild(editor.value);

    // MarkerJS emits this only when its native Undo manager commits a real state transition
    // (including pointer-up after a drag). The lower-level markerchange event can fire for every
    // animation frame, so it is deliberately not used to feed external history.
    editor.value.addEventListener("areastatechange", () => {
      if (markerStateCaptureBlocked === editorAtInitialization) {
        return;
      }
      if (markerEventBlocked(editorAtInitialization, "areastatechange")) {
        return;
      }
      captureCurrentImageState(editorAtInitialization);
    });

    editor.value.addEventListener(
      "markercreate",
      (evt: CustomEvent<MarkerEditorEventData>) => {
        // ls-heroku-ci (LSP-15699, round 2 finding #2): captureCurrentImageState()'s own
        // sourceEditor !== editor.value guard only protects its history write — it runs AFTER
        // repositionMarker() below, which unconditionally mutates the live editor's markers using
        // this stale event's marker/markerEditor. Hoisted here so nothing below can run on a
        // detached MarkerArea's queued event.
        if (markerEventBlocked(editorAtInitialization, "markercreate")) {
          return;
        }

        const marker = evt.detail.markerEditor.marker;
        repositionMarker(marker, evt.detail.markerEditor);

        if (marker instanceof TextMarker) {
          updateTextMarker(
            marker,
            evt.detail.markerEditor as TextMarkerEditor,
            true,
          );
        }

        // Reselect current marker only if it is a freehand marker

        if (
          evt.detail.markerEditor.marker.typeName === FreehandMarker.typeName
        ) {
          freehand(false);
        } else {
          currentEditorTypeName.value = null;
        }
      },
    );

    editor.value.addEventListener(
      "markerchange",
      (evt: CustomEvent<MarkerEditorEventData>) => {
        // ls-heroku-ci (LSP-15699, round 3, Low): consistency with the round-2 fix to
        // markercreate/markerselect/markerdeselect — updateTextMarker() below writes
        // lastTextMarker.value from whichever instance fired this event, stale or not.
        // captureCurrentImageState()'s own internal guard already fully covers this listener's only
        // OTHER effect, but hoisting the check here too keeps every marker listener consistently
        // guarded and logs a stale event instead of staying silent.
        if (markerEventBlocked(editorAtInitialization, "markerchange")) {
          return;
        }

        const marker = evt.detail.markerEditor.marker;

        if (marker instanceof TextMarker) {
          updateTextMarker(marker, evt.detail.markerEditor as TextMarkerEditor);
        }
      },
    );

    editor.value.addEventListener(
      "markerdelete",
      () => {
        // Keep the tracked snapshot current even when MarkerJS suppresses its own state-change
        // event while the deleted marker is still in edit mode.
        if (markerEventBlocked(editorAtInitialization, "markerdelete")) {
          return;
        }
        scheduleMarkerDeleteCapture(editorAtInitialization);
      },
    );

    editor.value.addEventListener(
      "markerdeselect",
      async (evt: CustomEvent<MarkerEditorEventData>) => {
        // ls-heroku-ci (LSP-15699, round 2 finding #2): deselectMarker() below calls
        // editor.value?.switchToSelectMode() on the CURRENT live editor unconditionally — a stale
        // event queued from a detached MarkerArea must not reach it.
        if (markerEventBlocked(editorAtInitialization, "markerdeselect")) {
          return;
        }

        const marker = evt.detail.markerEditor.marker;

        if (marker instanceof TextMarker) {
          updateTextMarker(marker, evt.detail.markerEditor as TextMarkerEditor);
        }

        deselectMarker();
      },
    );

    editor.value.addEventListener(
      "markerselect",
      (evt: CustomEvent<MarkerEditorEventData>) => {
        // ls-heroku-ci (LSP-15699, round 2 finding #2): currentSelectedMarker.value below is set
        // unconditionally from whichever instance fired this event — must not accept it from a
        // stale, detached MarkerArea.
        if (markerEventBlocked(editorAtInitialization, "markerselect")) {
          return;
        }

        currentSelectedMarker.value = evt.detail.markerEditor;
      },
    );

    if (wasShowingControls.value) {
      showControls.value = true;
      wasShowingControls.value = false;
    }

    // MarkerJS uses the current target dimensions to scale a restored snapshot. Establish them
    // first so an orientation change cannot restore markers at the snapshot's stale viewport size.
    setDimensions();
    if (currentState.value) {
      const stateBeingRestored = currentState.value;
      let restoreCompleted = false;
      let restoreObservationActive = true;
      let completedImageCheckTimer: ReturnType<typeof setTimeout> | undefined;
      let restoreWatchdogTimer: ReturnType<typeof setTimeout> | undefined;
      const markerAreaTargetImage =
        editorAtInitialization.shadowRoot?.querySelector<HTMLImageElement>(
          ".canvas-container img",
        );
      const onMarkerTargetLoad = () => {
        reportMissingRestoreCompletion(
          new Error(
            "MarkerJS annotation state restore did not complete after its target image loaded",
          ),
        );
      };
      const onMarkerTargetError = () => {
        reportMissingRestoreCompletion(
          new Error(
            "MarkerJS annotation target image failed to decode before state restore completed",
          ),
        );
      };
      const clearRestoreObservation = () => {
        restoreObservationActive = false;
        editorAtInitialization.removeEventListener(
          "arearestorestate",
          onRestoreCompleted,
        );
        markerAreaTargetImage?.removeEventListener("load", onMarkerTargetLoad);
        markerAreaTargetImage?.removeEventListener(
          "error",
          onMarkerTargetError,
        );
        if (completedImageCheckTimer !== undefined) {
          clearTimeout(completedImageCheckTimer);
        }
        if (restoreWatchdogTimer !== undefined) {
          clearTimeout(restoreWatchdogTimer);
        }
        if (cancelAnnotationRestoreObservation === clearRestoreObservation) {
          cancelAnnotationRestoreObservation = undefined;
        }
      };
      const onRestoreCompleted = () => {
        restoreCompleted = true;
        clearRestoreObservation();
        if (
          editorAtInitialization !== editor.value ||
          stateBeingRestored !== currentState.value
        ) {
          if (annotationRestoreOwner.value === editorAtInitialization) {
            annotationRestoreOwner.value = null;
          }
          console.warn(
            "ImageEditor: ignoring stale deferred annotation restore completion",
            {
              conversationId: props.conversationId,
              method: restoreFailureMethod,
            },
          );
          return;
        }
        if (annotationRestoreOwner.value === editorAtInitialization) {
          annotationRestoreOwner.value = null;
        }
        restoreCallbacks?.onSuccess?.();
      };

      // MarkerJS 3.9 queues restoreState() until its own cloned target image loads. Exceptions from
      // that later load listener cannot reach the synchronous try/catch below, and the library has
      // no restore-error event. Its documented arearestorestate event is therefore the completion
      // contract: after the private image's load listener has run, absence of that event means the
      // deferred restore failed. Listener registration order makes MarkerJS's listener run first;
      // the microtask also keeps this check correct if the browser dispatch implementation differs.
      const reportMissingRestoreCompletion = (error: Error) => {
        queueMicrotask(() => {
          // The target event may have queued this microtask immediately before clear(), teardown,
          // or a replacement initialization canceled the observation. Listener removal cannot
          // retract an already-queued callback, so honor the observation's explicit ownership too.
          if (!restoreObservationActive || restoreCompleted) return;
          clearRestoreObservation();
          const failedLiveRestore = failClosedAnnotationStateRestore(
            editorAtInitialization,
            stateBeingRestored,
            restoreFailureMethod,
            error,
          );
          if (failedLiveRestore) {
            restoreCallbacks?.onFailure?.();
          }
        });
      };
      cancelPendingAnnotationRestoreObservation();
      cancelAnnotationRestoreObservation = clearRestoreObservation;
      editorAtInitialization.addEventListener(
        "arearestorestate",
        onRestoreCompleted,
        { once: true },
      );
      markerAreaTargetImage?.addEventListener("load", onMarkerTargetLoad, {
        once: true,
      });
      markerAreaTargetImage?.addEventListener("error", onMarkerTargetError, {
        once: true,
      });
      restoreWatchdogTimer = setTimeout(() => {
        reportMissingRestoreCompletion(
          new Error(
            "MarkerJS annotation state restore timed out waiting for its target image and completion event",
          ),
        );
      }, ANNOTATION_RESTORE_TIMEOUT_MS);

      try {
        annotationRestoreOwner.value = editorAtInitialization;
        editorAtInitialization.restoreState(stateBeingRestored, false);
      } catch (err) {
        clearRestoreObservation();
        failClosedAnnotationStateRestore(
          editorAtInitialization,
          stateBeingRestored,
          restoreFailureMethod,
          err instanceof Error ? err : new Error(String(err)),
        );
        return false;
      }
      if (!restoreCompleted && markerAreaTargetImage?.complete) {
        // A cached image can already be complete before our listener is attached. Give any queued
        // native load/MarkerJS restore callbacks one macrotask to run first; only their continued
        // absence proves this completed target cannot release the lock itself.
        completedImageCheckTimer = setTimeout(() => {
          reportMissingRestoreCompletion(
            new Error(
              "MarkerJS annotation state restore did not complete for an already-loaded target image",
            ),
          );
        }, 0);
      }
    }

    // Reinitialize the cropper after an orientation change to correctly size and position the image

    if (cropping.value) {
      clearCropper(false);
      const cropStateAtResume = currentState.value;
      const editingSessionAtCropResume = editingSessionEpoch.value;
      const cropSessionAtResume = cropSessionEpoch.value;
      const cropResumeStillOwned = () =>
        editor.value === editorAtInitialization &&
        currentState.value === cropStateAtResume &&
        editingSessionEpoch.value === editingSessionAtCropResume &&
        cropSessionEpoch.value === cropSessionAtResume &&
        props.isEditing &&
        cropping.value;
      const initializeRestoredCropper = () => {
        if (!cropResumeStillOwned()) {
          console.warn(
            "ImageEditor: cropper restore resume skipped — editor or crop session changed before marker restoration completed",
            {
              conversationId: props.conversationId,
              restoreFailureMethod,
            },
          );
          return;
        }
        nextTick(async () => {
          if (!cropResumeStillOwned()) {
            console.warn(
              "ImageEditor: cropper restore resume skipped — editor or crop session changed before deferred initialization ran",
              {
                conversationId: props.conversationId,
                restoreFailureMethod,
              },
            );
            return;
          }
          await initializeCropper();
        });
      };
      if (annotationRestorePending.value) {
        // The crop raster must include the restored markers. Starting it while MarkerJS has only
        // staged the state would either rasterize an empty editor or hit the restore lock and exit
        // crop mode, so wait for the same completion signal that unlocks user actions.
        editorAtInitialization.addEventListener(
          "arearestorestate",
          initializeRestoredCropper,
          { once: true },
        );
      } else {
        initializeRestoredCropper();
      }
    }

    return true;
  }

  console.warn(
    "ImageEditor: initializeEditor skipped — editor container is unavailable",
    {
      conversationId: props.conversationId,
      restoreFailureMethod,
    },
  );
  if (reportUnavailable) {
    reportTargetImageFailure(
      new Error("ImageEditor container is unavailable"),
      restoreFailureMethod,
    );
  }
  return false;
}

// ls-heroku-ci (LSP-15699, round 3, Low): sourceEditor has no default -- all 5 call sites pass
// editorAtInitialization explicitly, and a default of editor.value would silently degrade the
// staleness guard below to a tautology (sourceEditor !== editor.value can never be true) if a
// future no-arg call were ever added.
function captureCurrentImageState(
  sourceEditor: MarkerArea | null,
  stateOverride?: AnnotationState,
) {
  if (!sourceEditor) {
    return;
  }

  // MarkerArea event callbacks can already be queued when Undo, a parent image swap, or teardown
  // detaches that editor. Only the currently-owned instance may seed the new session's history.
  if (sourceEditor !== editor.value) {
    return;
  }

  const previousState = currentState.value;
  const state = stateOverride ?? sourceEditor.getState();

  // MarkerJS can report one mutation through both areastatechange and a marker-specific listener.
  // One user action must create one application-level undo step.
  if (JSON.stringify(state) === JSON.stringify(previousState)) {
    return;
  }

  // Callers are MarkerJS's committed areastatechange signal and explicit marker mutations. Both
  // represent one completed user action, so no deep proxy serialization/diff is needed here.
  if (currentImageUrl.value) {
    pushImageOperationHistory(
      createImageOperationSnapshot(
        currentImageUrl.value,
        currentImageBlob.value,
        previousState,
        isDirty.value,
        cropSelectionChanged.value,
      ),
    );
  }

  currentState.value = state;
  const lastDecoded = lastDecodedImage.value;

  if (
    lastDecoded &&
    (lastDecoded.imageBlob
      ? lastDecoded.imageBlob === currentImageBlob.value
      : lastDecoded.imageUrl === currentImageUrl.value)
  ) {
    lastDecoded.state = state;
    // ls-heroku-ci (LSP-15699, round 3): keep this snapshot's history in sync with the push above —
    // otherwise a later restoreLastDecodedImage() call would restore this state but an
    // imageOperationHistory from before this marker mutation was ever recorded.
    lastDecoded.imageOperationHistory = imageOperationHistory.value.slice();
  }
}

function scheduleMarkerDeleteCapture(sourceEditor: MarkerArea) {
  pendingMarkerDeleteCapture = sourceEditor;
  if (markerDeleteCaptureQueued) return;

  markerDeleteCaptureQueued = true;
  queueMicrotask(() => {
    markerDeleteCaptureQueued = false;
    const editorToCapture = pendingMarkerDeleteCapture;
    pendingMarkerDeleteCapture = null;
    captureCurrentImageState(editorToCapture);
  });
}

type RotatableMarkerState = AnnotationState["markers"][number] & {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  rotationAngle?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  curveX?: number;
  curveY?: number;
  points?: IPoint[];
  visualTransformMatrix?: unknown;
  containerTransformMatrix?: unknown;
};

function rotatePointClockwise(point: IPoint, imageHeight: number): IPoint {
  return {
    x: imageHeight - point.y,
    y: point.x,
  };
}

// Rotates a preserved MarkerJS annotation state 90° clockwise so annotations stay editable through a
// Rotate instead of being flattened into the raster. Coordinates live in display (targetWidth/
// targetHeight) space; width/height are swapped and every marker's geometry is rotated about the old
// display box. restoreState() re-fits proportionally on the rotated image (see the onload handler's
// setDimensions() before the deferred restore), so the swap maps fractional positions exactly.
function rotateAnnotationStateClockwise(
  state: AnnotationState,
): AnnotationState {
  const imageWidth = state.width;
  const imageHeight = state.height;

  return {
    ...state,
    width: imageHeight,
    height: imageWidth,
    markers: state.markers.map((marker) => {
      const rotatedMarker = {
        ...marker,
      } as RotatableMarkerState;

      if (
        typeof rotatedMarker.left === "number" &&
        typeof rotatedMarker.top === "number" &&
        typeof rotatedMarker.width === "number" &&
        typeof rotatedMarker.height === "number"
      ) {
        const markerCenterX = rotatedMarker.left + rotatedMarker.width / 2;
        const markerCenterY = rotatedMarker.top + rotatedMarker.height / 2;
        const rotatedCenter = rotatePointClockwise(
          { x: markerCenterX, y: markerCenterY },
          imageHeight,
        );

        rotatedMarker.left = rotatedCenter.x - rotatedMarker.width / 2;
        rotatedMarker.top = rotatedCenter.y - rotatedMarker.height / 2;
        rotatedMarker.rotationAngle = (rotatedMarker.rotationAngle ?? 0) + 90;
        // MarkerJS serializes transforms that were derived from the old image's coordinate
        // system. Let restoreState recreate them from the rotated center and angle instead.
        delete rotatedMarker.visualTransformMatrix;
        delete rotatedMarker.containerTransformMatrix;
      }

      if (
        typeof rotatedMarker.x1 === "number" &&
        typeof rotatedMarker.y1 === "number" &&
        typeof rotatedMarker.x2 === "number" &&
        typeof rotatedMarker.y2 === "number"
      ) {
        const firstPoint = rotatePointClockwise(
          { x: rotatedMarker.x1, y: rotatedMarker.y1 },
          imageHeight,
        );
        const secondPoint = rotatePointClockwise(
          { x: rotatedMarker.x2, y: rotatedMarker.y2 },
          imageHeight,
        );
        rotatedMarker.x1 = firstPoint.x;
        rotatedMarker.y1 = firstPoint.y;
        rotatedMarker.x2 = secondPoint.x;
        rotatedMarker.y2 = secondPoint.y;
      }

      if (
        typeof rotatedMarker.curveX === "number" &&
        typeof rotatedMarker.curveY === "number"
      ) {
        const curvePoint = rotatePointClockwise(
          { x: rotatedMarker.curveX, y: rotatedMarker.curveY },
          imageHeight,
        );
        rotatedMarker.curveX = curvePoint.x;
        rotatedMarker.curveY = curvePoint.y;
      }

      if (rotatedMarker.points) {
        rotatedMarker.points = rotatedMarker.points.map((point) =>
          rotatePointClockwise(point, imageHeight),
        );
      }

      return rotatedMarker;
    }),
  };
}

async function rotateImageBy90DegreesClockwise() {
  // Mirrors resetDisabled's isSaving/cropperInitializing/confirmingEditorDiscard guards, but not
  // its crop-only confirmingCropDiscard clause, for a direct/programmatic call that bypasses the
  // disabled binding:
  // without this, Rotate could start its own render() concurrently with a pending Crop click's
  // render() (cropperInitializing), or clear() out from under an in-flight save (isSaving) via
  // updateTargetImage()'s unconditional isSaving reset.
  if (
    isSaving.value ||
    props.persistencePending ||
    cropperInitializing.value ||
    annotationRestorePending.value ||
    confirmingEditorDiscard.value
  ) {
    warnCropGuardSkipped("rotate");
    return;
  }

  // editor.value can be transiently null here: a prior updateTargetImage()/clear() call nulls it
  // synchronously, and the annotationImage decode that reassigns a fresh instance only resolves
  // later, asynchronously, inside its onload handler. Capturing that null as this call's lock
  // identity would make renderLockOwner indistinguishable from "no lock held" (cropperInitializing
  // requires renderLockOwner.value !== null), so the mutex would never engage for this call, and the
  // post-await staleness check below would misread the routine reassignment as the session having
  // ended, discarding a legitimate rotation. Bail out instead, matching initializeCropper()'s own
  // shadowRoot guard for the same decode window.
  if (!editor.value) {
    console.warn(
      "ImageEditor: rotate skipped — editor is not yet initialized (image still decoding)",
      {
        conversationId: props.conversationId,
        isEditing: props.isEditing,
      },
    );
    return;
  }

  // Also claims the shared render lock (the same one initializeCropper() uses) for its own render()
  // call below, so a Crop click landing while Rotate is mid-render sees cropperInitializing true too
  // — otherwise Crop's own guard (which only reads the lock, doesn't itself hold it during Rotate)
  // would see nothing in flight and start a second concurrent render() against the same editor/state.
  const lockOwner = editor.value;
  renderLockOwner.value = lockOwner;

  const state = currentState.value;

  try {
    // Rotate must preserve annotations as editable MarkerJS state (LSP-14181), never flatten them
    // into the raster via render() — so we always rotate the bare current image and re-apply the
    // rotated marker state below. A cross-image Undo exposes its retained raster through an object
    // URL that dataURLToBlob() cannot parse, so rotate the authoritative retained Blob directly.
    const src = currentImageUrl.value;
    if (!src) {
      throw new Error("Failed to get source image");
    }

    const blob = currentImageBlob.value ?? dataURLToBlob(src);
    if (!blob) {
      throw new Error("Failed to convert source image data URL to blob");
    }
    const finalBlob = await rotateBlob(blob);
    const finalDataURL = await blobToDataURL(finalBlob);

    // A discard/retake/teardown could complete while the awaits above were in flight, clearing or
    // replacing editor.value (and/or ending the session via isEditing). updateTargetImage() below
    // reassigns annotationImage.src, and its onload handler re-initializes the editor whenever
    // props.isEditing is true — without this re-check, a stale rotate finishing after the session
    // already ended would resurrect an editor for a session the user just left.
    if (editor.value !== lockOwner || !props.isEditing) {
      console.warn(
        "ImageEditor: rotate finished but the editing session ended while it was in flight — discarding the rotated image",
        {
          conversationId: props.conversationId,
          editorReplaced: editor.value !== lockOwner,
          isEditing: props.isEditing,
        },
      );
      return;
    }

    // Preserve editable annotations (clearState=false) and re-apply the rotated marker state so the
    // onload restore lands them at their rotated positions. updateTargetImage still records a
    // structural undo snapshot of the pre-rotate image/state (see shouldRecordStructuralSnapshot),
    // keeping the rotation undoable through imageOperationHistory. previousState is captured
    // synchronously before this assignment, so the snapshot holds the un-rotated markers.
    const targetImageUpdate = updateTargetImage(finalDataURL, false);
    if (state) {
      currentState.value = rotateAnnotationStateClockwise(state);
    }
    await targetImageUpdate;
    isDirty.value = true;
  } catch (err) {
    console.error(err, {
      tags: {
        component: "ImageEditor",
        method: "rotateImageBy90DegreesClockwise",
        conversationId: props.conversationId,
      },
    });
    const message = "Failed to rotate image";
    const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
    toast.error(message, { timeout });
  } finally {
    if (renderLockOwner.value === lockOwner) {
      renderLockOwner.value = null;
    }
  }
}

function updateColor(newColor: string) {
  annotationColor.value = newColor;
  setMarkerAttributes();
}

function toggleEdit() {
  showControls.value = !showControls.value;

  if (!showControls.value) {
    deselectMarker();
  }
}

/**
 * Whether a size is a real, usable dimension. The editor's coordinate space, the measurements that
 * derive it (see getEditorFitBox) and the stroke compensation that reads it (see adjustState) are
 * all meaningless for a zero, negative, NaN or infinite size.
 * @param size The size to check
 */
function isUsableSize(size: number | undefined | null): size is number {
  return typeof size === "number" && Number.isFinite(size) && size > 0;
}

function setDimensions() {
  if (!editor.value || !annotationImage.value) {
    return;
  }

  // Measured once and passed in so the diagnostics below report the very box the calculation used
  // (LSP-13917).
  const fitBox = getEditorFitBox();
  const [width, height] = calculateImageDimensions(
    annotationImage.value.naturalWidth,
    annotationImage.value.naturalHeight,
    fitBox,
  );

  // A transient zero/undecoded viewport (an orientation change reporting 0, or an image whose
  // natural size is not known yet) would otherwise assign a broken coordinate space. MarkerArea
  // applies targetWidth/targetHeight literally to its canvas (no fit-to-container of its own), and
  // markers are positioned in that space while Save's stroke compensation is measured against it,
  // so leave whatever the editor already has — an established space on a resize, or MarkerJS's own
  // default on a freshly created MarkerArea, which the annotationImage.onload handler then
  // re-drives once the image has decoded.
  if (!isUsableSize(width) || !isUsableSize(height)) {
    // An image that has not decoded yet (naturalWidth 0) is an expected transient — the decode
    // handlers call setDimensions again once it lands. A decoded width with an unusable computed
    // size is the case worth logging; the payload carries both the natural and the fit-box
    // dimensions because either side can be the culprit (an unmeasurable editor during an
    // orientation change, or a decoded width with no usable height).
    if (isUsableSize(annotationImage.value.naturalWidth)) {
      console.warn(
        "ImageEditor: setDimensions skipped — computed a non-usable image size",
        {
          conversationId: props.conversationId,
          width,
          height,
          naturalWidth: annotationImage.value.naturalWidth,
          naturalHeight: annotationImage.value.naturalHeight,
          fitBoxWidth: fitBox?.width,
          fitBoxHeight: fitBox?.height,
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
        },
      );
    }
    return;
  }

  editor.value.targetHeight = height;
  editor.value.targetWidth = width;
}

/**
 * Updates the current image that will be annotated and re-initializes the editor
 * @param imageUrl The new Base64 image URL
 * @param clearState Determines if the state should be cleared or preserved
 */
async function updateTargetImage(
  imageUrl: string | undefined,
  clearState: boolean = true,
  allowFailedImageRecovery: boolean = true,
  discardIfSupersededByParentTarget: boolean = false,
) {
  const previousImageUrl = currentImageUrl.value;
  const previousImageBlob = currentImageBlob.value;
  const previousState = currentState.value;
  const previousIsDirty = isDirty.value;
  const previousCropSelectionChanged = cropSelectionChanged.value;
  const previousImageOperationHistory = imageOperationHistory.value.slice();

  // A changed target URL is a structural image operation (Crop, Rotate) regardless of clearState:
  // Rotate now preserves editable annotations with clearState=false but must still be undoable, so
  // the snapshot condition keys off the URL change, not clearState. State-preserving reinitializations
  // (clearState=false with an unchanged URL) early-return below and never reach this snapshot.
  const shouldRecordStructuralSnapshot =
    !discardIfSupersededByParentTarget &&
    Boolean(previousImageUrl) &&
    imageUrl !== previousImageUrl;

  clear(clearState);

  if (!clearState && imageUrl === previousImageUrl) {
    const initialized = initializeEditor(
      "updateTargetImage state-preserving reinitialization",
      false,
    );
    if (!initialized) {
      throw new Error(
        "ImageEditor: updateTargetImage failed to reinitialize the editor while preserving state",
      );
    }
    return;
  }

  // Reset before the (possibly slow, network-driven) imageToDataUrl() conversion below, not just
  // before the final src reassignment — annotationImageLoaded must stop claiming the OLD image is
  // loaded for this call's entire duration, or a close/reopen of editing while a remote mediaUrl is
  // still converting would have the isEditing watcher's re-init branch (gated on this same flag)
  // reinitialize against an image that's already been superseded.
  annotationImageLoaded.value = false;

  targetImageRequestId.value++;
  const requestId = targetImageRequestId.value;
  if (discardIfSupersededByParentTarget) {
    latestParentTargetImageRequestId.value = requestId;
  }
  failedImageRecovery.value = {
    requestId,
    appliedRequestIdAtStart: targetImageAppliedRequestId.value,
    allowRecovery: allowFailedImageRecovery,
    imageUrl: previousImageUrl,
    imageBlob: previousImageBlob,
    state: previousState,
    isDirty: previousIsDirty,
    cropSelectionChanged: previousCropSelectionChanged,
    imageOperationHistory: previousImageOperationHistory,
  };

  try {
    if (!imageUrl?.startsWith("data:image")) {
      imageUrl = await imageToDataUrl(imageUrl);
    }
  } catch (err) {
    // The replacement editor owns its own load; ignore this cancelled editor's late rejection.
    if (!hasMounted) return;
    const recoveryWasNotSuperseded =
      failedImageRecovery.value?.requestId === requestId &&
      failedImageRecovery.value.appliedRequestIdAtStart ===
        targetImageAppliedRequestId.value;
    if (
      allowFailedImageRecovery &&
      !restoreFailedTargetImage(requestId) &&
      recoveryWasNotSuperseded
    ) {
      restoreLastDecodedImage();
    }
    // Parent target-image changes intentionally do not restore the previous editable image. This
    // component receives only URLs, not an artifact identity, so a failed request might be a switch
    // from photo A to photo B. Restoring A after B has become the parent's target would allow saving
    // the wrong photo under B's artifact; fail closed and let the host retry or close instead.
    throw err;
  }

  // A newer updateTargetImage() call (e.g. another targetImage prop change) can start and finish
  // its own imageToDataUrl() conversion while this one is still in flight — unlike rotate()/
  // initializeCropper(), which already check editor.value identity after their own awaits, nothing
  // here used to check whether THIS call was still the one the caller wanted before overwriting
  // currentImageUrl/annotationImage.src with what may now be stale data for an image nobody asked
  // for anymore. Compared against targetImageAppliedRequestId (not targetImageRequestId itself) —
  // a newer call merely having STARTED doesn't mean it will ever SUCCEED; discarding on that alone
  // would drop this call's perfectly good result if the newer one later rejects, leaving nothing
  // applied at all (see targetImageAppliedRequestId's own comment).
  if (
    requestId < targetImageAppliedRequestId.value ||
    (discardIfSupersededByParentTarget &&
      requestId !== latestParentTargetImageRequestId.value)
  ) {
    console.warn(
      "ImageEditor: updateTargetImage discarding a stale image conversion — a newer targetImage call has superseded it",
      {
        conversationId: props.conversationId,
        isEditing: props.isEditing,
      },
    );
    return;
  }

  if (discardIfSupersededByParentTarget) {
    // Clear the previous artifact's history only after the replacement conversion succeeds and
    // wins the request race. A network/CORS failure leaves the old image in place, so its Undo
    // timeline must remain usable too.
    imageOperationHistory.value = [];
  } else if (shouldRecordStructuralSnapshot && previousImageUrl) {
    pushImageOperationHistory(
      createImageOperationSnapshot(
        previousImageUrl,
        previousImageBlob,
        previousState,
        previousIsDirty,
        previousCropSelectionChanged,
        true,
      ),
    );
  }
  targetImageAppliedRequestId.value = requestId;
  currentImageUrl.value = imageUrl;
  currentImageBlob.value = imageUrl
    ? (dataURLToBlob(imageUrl) ?? undefined)
    : undefined;

  if (discardIfSupersededByParentTarget) {
    // Parent target swaps identify a different logical photo even when this component remains
    // mounted. Never let that photo reuse the prior artifact's durable-row identity.
    savePersistenceAttemptId = undefined;
  }

  if (annotationImage.value && imageUrl) {
    // Snapshot isEditing at the exact moment this decode starts (not annotationImageLoaded, which is
    // always false right here by construction) — the onload handler needs this to tell a genuine
    // stale-decode-outliving-session apart from a routine mount-closed load.
    decodeStartedWhileEditing.value = props.isEditing;
    beginImageObjectUrlTransition();
    setAnnotationImageSource(imageUrl, requestId);
  }
}

function restoreFailedTargetImage(
  requestId: number,
  replacementHadApplied: boolean = false,
): boolean {
  const recovery = failedImageRecovery.value;
  if (
    !recovery ||
    !recovery.allowRecovery ||
    recovery.requestId !== requestId ||
    requestId !== targetImageRequestId.value ||
    (!replacementHadApplied &&
      recovery.appliedRequestIdAtStart !== targetImageAppliedRequestId.value) ||
    !recovery.imageUrl ||
    !annotationImage.value
  ) {
    console.warn(
      "ImageEditor: failed-image recovery skipped — recovery is no longer safe",
      {
        conversationId: props.conversationId,
        hasRecovery: Boolean(recovery),
        allowsRecovery: recovery?.allowRecovery,
        requestMatches: recovery?.requestId === requestId,
        latestRequestMatches: requestId === targetImageRequestId.value,
        replacementHadApplied,
        hasImageUrl: Boolean(recovery?.imageUrl),
        hasAnnotationImage: Boolean(annotationImage.value),
      },
    );
    return false;
  }

  // The replacement never became usable, so put the known-good image and its annotations back
  // through the normal onload path. Clear this first: if restoring that older image also fails,
  // do not loop indefinitely trying the same broken source.
  cancelImageObjectUrlTransition();
  failedImageRecovery.value = undefined;
  currentImageUrl.value = recovery.imageUrl;
  currentImageBlob.value = recovery.imageBlob;
  currentState.value = recovery.state;
  isDirty.value = recovery.isDirty;
  cropSelectionChanged.value = recovery.cropSelectionChanged;
  imageOperationHistory.value = [...recovery.imageOperationHistory];
  annotationImageLoaded.value = false;
  decodeStartedWhileEditing.value = props.isEditing;
  setAnnotationImageSource(recovery.imageUrl, requestId);
  return true;
}

function restoreLastDecodedImage(): boolean {
  const lastDecoded = lastDecodedImage.value;
  if (
    !lastDecoded ||
    lastDecoded.imageUrl === currentImageUrl.value ||
    !annotationImage.value
  ) {
    console.warn(
      "ImageEditor: last decoded image recovery skipped — recovery is no longer safe",
      {
        conversationId: props.conversationId,
        hasLastDecodedImage: Boolean(lastDecoded),
        alreadyCurrentImage: lastDecoded?.imageUrl === currentImageUrl.value,
        hasAnnotationImage: Boolean(annotationImage.value),
      },
    );
    return false;
  }

  cancelImageObjectUrlTransition();
  failedImageRecovery.value = undefined;
  currentImageUrl.value = lastDecoded.imageUrl;
  currentImageBlob.value = lastDecoded.imageBlob;
  currentState.value = lastDecoded.state;
  isDirty.value = lastDecoded.isDirty;
  cropSelectionChanged.value = lastDecoded.cropSelectionChanged;
  // ls-heroku-ci (LSP-15699, round 3, Critical): without this, imageOperationHistory stays whatever
  // it was left at (already popped by the undo/op this is recovering from), desyncing the undo
  // stack from the image/state just restored above — the next Undo would pop the wrong snapshot.
  imageOperationHistory.value = [...lastDecoded.imageOperationHistory];
  annotationImageLoaded.value = false;
  decodeStartedWhileEditing.value = props.isEditing;
  setAnnotationImageSource(
    lastDecoded.imageUrl,
    targetImageAppliedRequestId.value,
  );
  return true;
}

function recoverOrReleaseFailedTargetImage(
  requestId: number,
  replacementHadApplied: boolean,
): boolean {
  if (failedImageRecovery.value?.requestId !== requestId) {
    console.warn(
      "ImageEditor: failed image recovery skipped — a newer request owns recovery",
      {
        conversationId: props.conversationId,
        failedRequestId: requestId,
        recoveryRequestId: failedImageRecovery.value?.requestId,
      },
    );
    return false;
  }

  if (
    restoreFailedTargetImage(requestId, replacementHadApplied) ||
    restoreLastDecodedImage()
  ) {
    return true;
  }

  // The request still owns its recovery record, but neither source can rebuild a usable editor.
  // Do not leave its Blob transition as a permanent Undo/action lock. The editor stays fail-closed
  // until another target applies or the user exits through Cancel.
  cancelImageObjectUrlTransition();
  failedImageRecovery.value = undefined;
  return false;
}

function reportTargetImageFailure(err: unknown, method: string) {
  console.error(err, {
    tags: {
      component: "ImageEditor",
      method,
      conversationId: props.conversationId,
    },
  });
  const message = "Failed to load image";
  const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
  toast.error(message, { timeout });
}

async function imageToDataUrl(imageUrl: string | undefined): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!imageUrl) {
      reject(new Error("Failed to get image URL"));
      return;
    }

    const canvas = helperCanvas.value;

    if (!canvas) {
      reject(new Error("Failed to get canvas"));
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Failed to get 2D context"));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous"; // required for CORS-safe image to allow toDataURL
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      try {
        // PNG so we don't lose any data
        const dataUrl = canvas.toDataURL("image/png");
        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = imageUrl;
  });
}

interface EditorFitBox {
  width: number;
  height: number;
}

/**
 * The box the edited image is fitted into.
 *
 * LSP-13917: this deliberately no longer reads window.innerWidth/innerHeight first. On iOS/iPadOS
 * Safari those track the VISUAL viewport, so they shrink while the user is pinch-zoomed — and
 * MarkerJS explicitly opts its own canvas container into browser pinch-zoom (it sets
 * `touch-action: pinch-zoom` on `.canvas-container`, which initializeEditor() now overrides). Any
 * layout rebuild that ran while the page was zoomed therefore baked the zoomed-in (much smaller)
 * viewport into MarkerArea's targetWidth/targetHeight, and because MarkerArea applies those
 * literally (its zoomLevel stays 1 and autoZoom is off) the image stayed rendered tiny once the
 * zoom reset — the "really zoomed out" photo reported after a window resize followed by an iPad
 * rotation.
 *
 * Measurement order, most to least trustworthy:
 *  1. the editor overlay's own box — the element the image actually has to fit inside;
 *  2. the LAYOUT viewport (documentElement), which is what the editor's CSS (`100vw` / `100%` of a
 *     full-screen overlay) is sized against and is immune to pinch zoom;
 *  3. window.innerWidth/innerHeight, only as a last resort for environments that measure 0 for
 *     both of the above.
 *
 * Returns null when nothing measurable is available (a `v-show`-hidden overlay in a zero-size test
 * environment, a detached document) so callers can skip rather than write 0/NaN dimensions.
 */
function getEditorFitBox(): EditorFitBox | null {
  const overlay = editContainer.value;
  if (
    overlay &&
    isUsableSize(overlay.clientWidth) &&
    isUsableSize(overlay.clientHeight)
  ) {
    return { width: overlay.clientWidth, height: overlay.clientHeight };
  }

  const layoutViewport = document.documentElement;
  if (
    layoutViewport &&
    isUsableSize(layoutViewport.clientWidth) &&
    isUsableSize(layoutViewport.clientHeight)
  ) {
    return {
      width: layoutViewport.clientWidth,
      height: layoutViewport.clientHeight,
    };
  }

  // Last resort, and the only reading here that can be pinch-contaminated. window.inner* track the
  // VISUAL viewport on iOS, so they are the layout box divided by the pinch scale; multiplying by
  // that same scale is what undoes the division and recovers the layout-sized box this fallback
  // stands in for (break-it round 1).
  //
  // Applied for scale < 1 as well as scale > 1: a zoomed-OUT visual viewport makes window.inner*
  // larger than the layout box, and the same multiplication corrects that overestimate too.
  // Previously only scale > 1 was corrected, which left the zoomed-out case overestimating.
  const visualViewportScale = window.visualViewport?.scale ?? 1;
  const pinchCorrection = isUsableSize(visualViewportScale)
    ? visualViewportScale
    : 1;
  if (
    isUsableSize(window.innerWidth) &&
    isUsableSize(window.innerHeight)
  ) {
    return {
      width: window.innerWidth * pinchCorrection,
      height: window.innerHeight * pinchCorrection,
    };
  }

  console.warn(
    "ImageEditor: editor fit box unavailable — no usable overlay, layout viewport, or window measurement",
    {
      conversationId: props.conversationId,
      isEditing: props.isEditing,
      overlayWidth: overlay?.clientWidth,
      overlayHeight: overlay?.clientHeight,
      layoutViewportWidth: layoutViewport?.clientWidth,
      layoutViewportHeight: layoutViewport?.clientHeight,
      windowInnerWidth: window.innerWidth,
      windowInnerHeight: window.innerHeight,
    },
  );
  return null;
}

/**
 * Calculates the dimensions of the image as it will be displayed on the screen based on the
 * available editor dimensions
 * @param imageWidth The image width
 * @param imageHeight The image height
 * @param fitBox The box to fit the image into. Defaults to the current editor fit box; pass an
 * already-measured box to keep several calculations in one layout pass consistent with each other.
 * @returns An array containing the image width (index 0) and height (index 1), or [0, 0] when the
 * inputs cannot produce a real size
 */
function calculateImageDimensions(
  imageWidth: number,
  imageHeight: number,
  fitBox: EditorFitBox | null = getEditorFitBox(),
): Array<number> {
  // An undecoded image reports naturalWidth/naturalHeight of 0, which previously produced a NaN
  // aspect ratio and a NaN targetWidth on the MarkerArea. Refuse instead of propagating it.
  //
  // Deliberately not warned here: a not-yet-decoded image is an expected, very frequent transient
  // state (initializeEditor() runs before the decode; the decode's own onload re-runs
  // setDimensions()), so logging it would bury real signal rather than add it. The genuinely
  // anomalous halves are each logged by the caller that can tell them apart — getEditorFitBox()
  // reports an unmeasurable editor, and setDimensions() reports a decoded image that still produced
  // an unusable size (LSP-16400).
  if (!fitBox || !isUsableSize(imageWidth) || !isUsableSize(imageHeight)) {
    return [0, 0];
  }

  const maxWidth = fitBox.width;
  const maxHeight = fitBox.height;
  const imageAspectRatio = imageWidth / imageHeight;
  const maxAspectRatio = maxWidth / maxHeight;

  let targetWidth, targetHeight;

  if (imageAspectRatio > maxAspectRatio) {
    // Image is relatively wider than container. Make sure that the image width always fills up the width of the screen
    targetWidth = Math.max(maxWidth, Math.min(imageWidth, maxWidth));
    targetHeight = targetWidth / imageAspectRatio;
  } else {
    // Image is relatively taller than container. Make sure that the image width always fills up the height of the screen
    targetHeight = Math.max(maxHeight, Math.min(imageHeight, maxHeight));
    targetWidth = targetHeight * imageAspectRatio;
  }

  return [targetWidth, targetHeight];
}

// Both offsets, always. `axis` names whichever one actually has slack; it never gates which offset
// is emitted (see calculateEditorCenteringOffset). Kept because it makes the returned pair
// self-describing at a glance, but note that nothing currently reads it — only .top/.left are
// consumed — so do not rely on it being maintained as a contract.
type EditorCenteringOffset = {
  axis: "vertical" | "horizontal";
  top: number;
  left: number;
};

/**
 * The manual centring offset iOS/Safari needs for the fitted image (see initializeEditor).
 *
 * LSP-13917: the offsets are derived from the box the image was actually fitted into, NOT from
 * `screen.orientation` and NOT from `window.inner*`. calculateImageDimensions() fills one axis of
 * the box exactly and leaves all the slack on the other, and which axis that is depends on the
 * image's aspect ratio versus the BOX's — not on which way the device is turned. An iPad Split View
 * / Stage Manager pane can be tall and narrow on a landscape device (and short and wide on a
 * portrait one), so the old orientation-based branch offset the axis that had no slack at all and
 * left the photo pinned to the top (or left) edge of the pane.
 *
 * LSP-18569: BOTH offsets are returned and both are emitted. Centring only the larger-slack axis
 * relied on calculateImageDimensions() leaving the other axis with exactly zero slack; sub-pixel
 * fitting and a future fit branch can both break that, and a missing offset shows up as the photo
 * pinned to an edge on iPad Safari. Emitting both is unconditionally correct — the axis with no
 * slack simply gets an offset of 0.
 * @param fitBox The box the image was fitted into
 * @param imageWidth The fitted image width
 * @param imageHeight The fitted image height
 */
function calculateEditorCenteringOffset(
  fitBox: EditorFitBox,
  imageWidth: number,
  imageHeight: number,
): EditorCenteringOffset {
  // Clamped at 0: a fitted size can exceed the box by a fraction of a pixel, and a negative offset
  // would push the image (and the marker overlay pinned to the same offset) off the visible edge.
  const verticalSlack = Math.max(fitBox.height - imageHeight, 0);
  const horizontalSlack = Math.max(fitBox.width - imageWidth, 0);
  // A tie means the image fills the box on both axes (its aspect ratio matches), so both offsets are
  // 0 and either label is correct — horizontal keeps the reported axis the same it has always been
  // for an exactly-filled box.
  return {
    axis: verticalSlack > horizontalSlack ? "vertical" : "horizontal",
    top: verticalSlack / 2,
    left: horizontalSlack / 2,
  };
}

function isMarkerSelected(typeName: string) {
  let selected: boolean = false;
  selected = typeName === currentEditorTypeName.value;
  return selected;
}

/**
 * Steps back through the image-operation history (LSP-15699): restores the image/marker state as
 * it was immediately before the most recent undoable action — a marker create/change/delete, a
 * Crop Apply, or a Rotate. This is the ONLY undo mechanism now; editor.value's own (per-instance)
 * undo stack is never consulted, because it can't survive a Crop/Rotate — both always discard the
 * old MarkerArea instance and start a fresh one with an empty stack, so relying on it would make
 * Undo permanently dead the moment either op ran once. See captureCurrentImageState() for where
 * marker-level entries get pushed, and updateTargetImage() for Crop Apply/Rotate's.
 */
function undo(): boolean {
  // Mirrors reset()'s/rotate()'s own guards: an image-history restore reassigns
  // annotationImage.src just like they do, so it must not race a save, an in-flight crop
  // initialization, or a pending discard/crop confirmation. cropping.value itself is included
  // because the Undo control isn't reachable while cropping (its toolbar is covered by the crop
  // overlay), but a stale/queued click landing after Crop mode opens shouldn't act anyway.
  if (undoActionBlocked.value) {
    warnCropGuardSkipped("undo (image operation)");
    return false;
  }

  // Starting another cross-image Undo before the first image decodes would overwrite its
  // failed-image recovery source. This applies equally to Blob/object URLs and plain URL-backed
  // history entries, so it cannot be inferred from the object-URL transition alone.
  if (crossImageUndoPending.value) {
    console.warn(
      "ImageEditor: undo skipped — a cross-image restore is still decoding",
      {
        conversationId: props.conversationId,
        pendingImageUrl: currentImageUrl.value,
      },
    );
    return false;
  }

  const snapshot = imageOperationHistory.value.at(-1);
  if (!snapshot || !annotationImage.value) {
    // ls-heroku-ci (LSP-15699, finding #5): every other guard in this function logs via
    // warnCropGuardSkipped(); this one was a bare return with nothing to go on for a future
    // "Undo did nothing" report, in the exact function this PR rewrote the failure paths of.
    console.warn("ImageEditor: undo skipped — no history entry to restore", {
      conversationId: props.conversationId,
      hasSnapshot: Boolean(snapshot),
      hasAnnotationImage: Boolean(annotationImage.value),
    });
    return false;
  }

  const imageUrlBeforeUndo = currentImageUrl.value;
  if (!imageUrlBeforeUndo) {
    console.warn(
      "ImageEditor: undo skipped — no current image to recover to on failure",
      {
        conversationId: props.conversationId,
      },
    );
    return false;
  }

  // Cursor Bugbot (LSP-15699, round 3): must run before failedImageRecovery is (re)assigned and
  // before the pop below — this bail leaves everything untouched on purpose. The earlier version of
  // this check ran after both, so a blocked second Undo would overwrite the still-pending FIRST
  // Undo's own failedImageRecovery with this call's (never-applied) one, erasing the real in-flight
  // recovery record; if that first decode then failed, there would be nothing left to roll back to.
  // isSameImage means the CURRENT image (the one annotationImage.value's src already points at) is
  // what we're restoring to — but if a previous Undo's own async setAnnotationImageSource() decode
  // for that exact src is still in flight (annotationImageLoaded still false), the sync branch below
  // would call initializeEditor()/setDimensions() against natural dimensions that haven't decoded
  // yet (NaN aspect ratio), and the real decode's eventual onload would be rejected by the epoch
  // this Undo's own clear(false) is about to bump — nothing would ever repair it. Only isSameImage
  // needs this: the else branch always starts its own fresh decode and waits for it regardless.
  const isSameImage = snapshot.imageBlob
    ? snapshot.imageBlob === currentImageBlob.value
    : snapshot.imageUrl === imageUrlBeforeUndo;
  if (isSameImage && !annotationImageLoaded.value) {
    console.warn(
      "ImageEditor: undo skipped — current image is still decoding from a prior operation",
      {
        conversationId: props.conversationId,
      },
    );
    return false;
  }

  // A cross-image Undo starts its own decode request. Give it a fresh id so a failure cannot be
  // mistaken for (or recover through) the prior Crop/Rotate request that produced the current
  // image.
  if (!isSameImage) {
    targetImageRequestId.value++;
  }
  const undoRequestId = targetImageRequestId.value;
  failedImageRecovery.value = {
    requestId: undoRequestId,
    appliedRequestIdAtStart: targetImageAppliedRequestId.value,
    allowRecovery: true,
    imageUrl: imageUrlBeforeUndo,
    imageBlob: currentImageBlob.value,
    state: currentState.value,
    isDirty: isDirty.value,
    cropSelectionChanged: cropSelectionChanged.value,
    imageOperationHistory: imageOperationHistory.value.slice(),
  };
  imageOperationHistory.value = imageOperationHistory.value.slice(0, -1);

  // Same restore shape as restoreLastDecodedImage()/restoreFailedTargetImage() above: reassign the
  // state directly and let the existing onload path (setAnnotationImageSource) reinitialize the
  // editor and restore markers via initializeEditor()'s currentState.value check.
  // Detach the old MarkerArea immediately so Crop/Rotate cannot consume stale pixels/state while
  // the restored image decodes. clearState=false preserves the snapshot assigned just below.
  // Cursor Bugbot (LSP-15699): a marker-only history entry never changed the underlying image,
  // so snapshot.imageUrl === imageUrlBeforeUndo. Reassigning annotationImage.src to the exact
  // same value it already holds does not reliably refire "load" in Chromium/WebKit — the editor
  // would stay permanently null (clear(false) already nulled it, and nothing else reinitializes
  // it). The image is already decoded, so skip the decode-event path entirely and reinitialize
  // synchronously instead of waiting for an event that will never come.

  const restoredObjectUrl =
    !isSameImage && snapshot.imageBlob
      ? URL.createObjectURL(snapshot.imageBlob)
      : undefined;
  const restoredImageUrl = isSameImage
    ? imageUrlBeforeUndo
    : (restoredObjectUrl ?? snapshot.imageUrl);
  if (!restoredImageUrl) {
    console.warn("ImageEditor: undo skipped — history image is unavailable", {
      conversationId: props.conversationId,
    });
    imageOperationHistory.value = [...imageOperationHistory.value, snapshot];
    failedImageRecovery.value = undefined;
    return false;
  }

  if (isSameImage && restoreSameImageUndoInPlace(snapshot)) {
    return true;
  }

  clear(false);
  if (!isSameImage) {
    crossImageUndoPending.value = true;
  }
  currentImageUrl.value = restoredImageUrl;
  currentImageBlob.value = snapshot.imageBlob;
  currentState.value = snapshot.state;
  isDirty.value = snapshot.isDirty;
  // Deliberately always false, not snapshot.cropSelectionChanged: this flag is only meaningful
  // while actively adjusting an open crop selection, and undo() never leaves cropping.value true
  // (Undo isn't reachable while cropping, and never re-enters it) — restoring a stale `true` from a
  // pre-crop-Apply snapshot would leave it stuck true outside crop mode with nothing to reset it,
  // making hasUnsavedEditorWork() return true forever: spurious discard confirms, a wrong
  // forceDiscard computation, and onBeforeUnload blocking tab close even with no visible unsaved work.
  cropSelectionChanged.value = false;

  if (isSameImage) {
    // ls-heroku-ci (LSP-15699, round 3): mirrors the try/catch setAnnotationImageSource's own
    // image.onload wraps this identical initializeEditor()/setDimensions() pair in — initializeEditor()
    // calls editor.value.restoreState() (~1170), a markerjs3 call that can throw on a stale/incompatible
    // snapshot. Without this, an exception here would propagate uncaught out of the @click="undo"
    // handler with no log, right after failedImageRecovery was cleared with nothing left to fall back to.
    let undoInitializationFinalized = false;
    const finalizeSameImageUndo = () => {
      if (undoInitializationFinalized) return;
      undoInitializationFinalized = true;
      failedImageRecovery.value = undefined;
      // The async branch's onload handler refreshes lastDecodedImage after a successful restore.
      // Same-image Undo has no outer decode, so its arearestorestate completion owns that commit.
      if (currentImageUrl.value) {
        lastDecodedImage.value = {
          imageUrl: currentImageUrl.value,
          imageBlob: currentImageBlob.value,
          state: currentState.value,
          isDirty: isDirty.value,
          cropSelectionChanged: cropSelectionChanged.value,
          imageOperationHistory: imageOperationHistory.value.slice(),
        };
      }
    };
    const restoreLastDecodedSameImageDirectly = (): boolean => {
      const lastDecoded = lastDecodedImage.value;
      if (!lastDecoded || !annotationImage.value) {
        console.warn(
          "ImageEditor: direct same-image last-decoded recovery skipped — recovery is no longer safe",
          {
            conversationId: props.conversationId,
            hasLastDecodedImage: Boolean(lastDecoded),
            hasAnnotationImage: Boolean(annotationImage.value),
          },
        );
        failedImageRecovery.value = undefined;
        return false;
      }

      clear(false);
      currentImageUrl.value = lastDecoded.imageUrl;
      currentImageBlob.value = lastDecoded.imageBlob;
      currentState.value = lastDecoded.state;
      isDirty.value = lastDecoded.isDirty;
      cropSelectionChanged.value = lastDecoded.cropSelectionChanged;
      imageOperationHistory.value = [...lastDecoded.imageOperationHistory];
      annotationImageLoaded.value = true;
      const finishLastDecodedRecovery = () => {
        failedImageRecovery.value = undefined;
      };
      const lastDecodedRestored = initializeEditor(
        "undo (deferred same-image last-decoded recovery)",
        true,
        {
          onSuccess: finishLastDecodedRecovery,
          onFailure: finishLastDecodedRecovery,
        },
      );
      if (!lastDecodedRestored) {
        finishLastDecodedRecovery();
        return false;
      }
      setDimensions();
      if (!annotationRestorePending.value) {
        finishLastDecodedRecovery();
      }
      return true;
    };
    const rebuildPreUndoSameImageState = (): boolean => {
      const recovery = failedImageRecovery.value;
      if (!recovery?.imageUrl) {
        return restoreLastDecodedSameImageDirectly();
      }

      // This is the same already-decoded raster. Reassigning annotationImage.src is unreliable
      // in Chromium/WebKit, so restore the pre-Undo fields and rebuild MarkerArea directly.
      clear(false);
      currentImageUrl.value = recovery.imageUrl;
      currentImageBlob.value = recovery.imageBlob;
      currentState.value = recovery.state;
      isDirty.value = recovery.isDirty;
      cropSelectionChanged.value = recovery.cropSelectionChanged;
      imageOperationHistory.value = [...recovery.imageOperationHistory];
      annotationImageLoaded.value = true;
      const finishPreUndoRecovery = () => {
        failedImageRecovery.value = undefined;
        if (currentImageUrl.value) {
          lastDecodedImage.value = {
            imageUrl: currentImageUrl.value,
            imageBlob: currentImageBlob.value,
            state: currentState.value,
            isDirty: isDirty.value,
            cropSelectionChanged: cropSelectionChanged.value,
            imageOperationHistory: imageOperationHistory.value.slice(),
          };
        }
      };
      const recoveryRestored = initializeEditor(
        "undo (deferred same-image recovery reinit)",
        true,
        {
          onSuccess: finishPreUndoRecovery,
          onFailure: restoreLastDecodedSameImageDirectly,
        },
      );
      if (!recoveryRestored) {
        return restoreLastDecodedSameImageDirectly();
      }
      setDimensions();
      if (!annotationRestorePending.value) {
        finishPreUndoRecovery();
      }
      return true;
    };
    const recoverDeferredSameImageUndo = () => {
      rebuildPreUndoSameImageState();
    };
    try {
      annotationImageLoaded.value = true;
      const restored = initializeEditor(
        "undo (isSameImage sync reinit)",
        true,
        {
          onSuccess: finalizeSameImageUndo,
          onFailure: recoverDeferredSameImageUndo,
        },
      );
      if (!restored) {
        rebuildPreUndoSameImageState();
        return false;
      }
      setDimensions();
      if (!annotationRestorePending.value) {
        // A synchronous MarkerJS restore already called the callback above. No-state restores have
        // no arearestorestate event, so finalize them explicitly here.
        finalizeSameImageUndo();
      }
    } catch (err) {
      reportTargetImageFailure(err, "undo (isSameImage sync reinit)");
      try {
        rebuildPreUndoSameImageState();
      } catch (recoveryError) {
        reportTargetImageFailure(
          recoveryError,
          "undo (isSameImage thrown-error recovery)",
        );
      }
      return false;
    }
  } else {
    annotationImageLoaded.value = false;
    decodeStartedWhileEditing.value = props.isEditing;
    beginImageObjectUrlTransition(restoredObjectUrl);
    setAnnotationImageSource(restoredImageUrl, undoRequestId);
  }
  return true;
}

/**
 * LSP-20947: a marker-only Undo keeps the same decoded image, so rebuilding the MarkerArea (wiping
 * the container, re-cloning the target image, showing the restore spinner, then re-adding markers
 * once the clone loads) is what made Undo visibly flash. MarkerJS restores synchronously on a live
 * editor whose target image has loaded, so apply the snapshot there instead. Returns false without
 * committing anything when the live restore is unavailable, throws, or is deferred by MarkerJS; the
 * caller then falls back to the rebuild path, which owns the async restore and failure recovery.
 */
function restoreSameImageUndoInPlace(
  snapshot: ImageOperationSnapshot,
): boolean {
  const liveEditor = editor.value;
  if (!liveEditor) {
    console.warn(
      "ImageEditor: in-place undo skipped — no live editor to restore into",
      {
        conversationId: props.conversationId,
      },
    );
    return false;
  }

  const restoredState = snapshot.state ?? {
    ...liveEditor.getState(),
    markers: [],
  };
  let restoredSynchronously = false;
  const onRestored = () => {
    restoredSynchronously = true;
  };
  liveEditor.addEventListener("arearestorestate", onRestored, { once: true });
  // addUndoStep=true re-baselines MarkerJS's native Undo stack on the restored state, so its next
  // no-op addUndoStep() (e.g. selecting a marker) compares equal and emits nothing. That re-baseline
  // itself emits areastatechange, which is a restore, not a user edit — suppress its capture.
  const previousCaptureBlock = markerStateCaptureBlocked;
  markerStateCaptureBlocked = liveEditor;
  try {
    deselectMarker();
    liveEditor.restoreState(restoredState, true);
  } catch (err) {
    console.warn(
      "ImageEditor: in-place undo failed — rebuilding the editor instead",
      {
        conversationId: props.conversationId,
        error: err instanceof Error ? err.message : String(err),
      },
    );
    return false;
  } finally {
    markerStateCaptureBlocked = previousCaptureBlock;
    liveEditor.removeEventListener("arearestorestate", onRestored);
  }

  if (!restoredSynchronously) {
    console.warn(
      "ImageEditor: in-place undo deferred by MarkerJS — rebuilding the editor instead",
      {
        conversationId: props.conversationId,
      },
    );
    return false;
  }

  currentState.value = snapshot.state;
  isDirty.value = snapshot.isDirty;
  // Same reasoning as undo()'s rebuild path: this flag only has meaning inside an open crop.
  cropSelectionChanged.value = false;
  failedImageRecovery.value = undefined;
  if (currentImageUrl.value) {
    lastDecodedImage.value = {
      imageUrl: currentImageUrl.value,
      imageBlob: currentImageBlob.value,
      state: currentState.value,
      isDirty: isDirty.value,
      cropSelectionChanged: cropSelectionChanged.value,
      imageOperationHistory: imageOperationHistory.value.slice(),
    };
  }
  return true;
}

function dot(userClicked: boolean = true) {
  if (markerActionBlocked("dot")) return;
  if (userClicked && currentEditorTypeName.value === DotMarker.typeName) {
    deselectMarker();
    return;
  }

  createMarkerWithoutCapturingToolSelection(DotMarker);
  currentEditorTypeName.value = DotMarker.typeName;
  setMarkerAttributes();
}

function freehand(userClicked: boolean = true) {
  if (markerActionBlocked("freehand")) return;
  if (userClicked && currentEditorTypeName.value === FreehandMarker.typeName) {
    deselectMarker();
    return;
  }

  currentSelectedMarker.value =
    createMarkerWithoutCapturingToolSelection(FreehandMarker);
  currentEditorTypeName.value = FreehandMarker.typeName;
  setMarkerAttributes();
}

function lineMarker(userClicked: boolean = true) {
  if (markerActionBlocked("line")) return;
  if (userClicked && currentEditorTypeName.value === LineMarker.typeName) {
    deselectMarker();
    return;
  }

  createMarkerWithoutCapturingToolSelection(LineMarker);
  currentEditorTypeName.value = LineMarker.typeName;
  setMarkerAttributes();
}

function arrowMarker(userClicked: boolean = true) {
  if (markerActionBlocked("arrow")) return;
  if (userClicked && currentEditorTypeName.value === ArrowMarker.typeName) {
    deselectMarker();
    return;
  }

  createMarkerWithoutCapturingToolSelection(ArrowMarker);
  currentEditorTypeName.value = ArrowMarker.typeName;
  setMarkerAttributes();
}

function deselectMarker() {
  editor.value?.switchToSelectMode();
  currentEditorTypeName.value = null;
  currentSelectedMarker.value = undefined;
}

function createMarkerWithoutCapturingToolSelection(
  markerType: typeof MarkerBase,
): MarkerBaseEditor | undefined {
  const activeEditor = editor.value;
  if (!activeEditor) return undefined;

  // LSP-20947: MarkerJS calls addUndoStep() synchronously inside createMarker() before the user
  // draws anything. That native baseline emits areastatechange, but selecting a toolbar tool is
  // not an application-level edit. Suppress only that synchronous setup event; marker creation,
  // manipulation, and pointer-up happen later and continue through the normal history listener.
  const previousCaptureBlock = markerStateCaptureBlocked;
  markerStateCaptureBlocked = activeEditor;
  try {
    return activeEditor.createMarker(markerType);
  } finally {
    markerStateCaptureBlocked = previousCaptureBlock;
  }
}

/**
 * * @deprecated Leave this in place in case we want to revert to auto-placing the arrow marker
 * Auto adds the linear marker to the annotation canvas
 * @param markerEditor The marker editor/marker currently being created
 */
function autoPlaceLinearMarker(markerEditor: LinearMarkerEditor): void {
  if (!markerEditor) {
    console.warn("LinearMarkerEditor. Not autoplacing");
    return;
  }

  nextTick(() => {
    if (annotationImage.value) {
      const [width, height] = calculateImageDimensions(
        annotationImage.value.naturalWidth,
        annotationImage.value.naturalHeight,
      );
      // getEditorFitBox() logs that it could not measure anything, but not who asked. Without this
      // the arrow is placed at (-75, 0) — off the canvas — and the only clue is a generic warning
      // several frames earlier (LSP-13917 review).
      if (!isUsableSize(width) || !isUsableSize(height)) {
        console.warn(
          "ImageEditor: autoPlaceLinearMarker got an unusable fitted size — placing the arrow would put it off-canvas",
          {
            conversationId: props.conversationId,
            width,
            height,
            naturalWidth: annotationImage.value.naturalWidth,
            naturalHeight: annotationImage.value.naturalHeight,
          },
        );
        return;
      }
      let x = width / 2 - 75;
      let y = height / 2;
      markerEditor.pointerDown({ x: x, y: y } as IPoint);
      markerEditor.marker.x1 = x;
      markerEditor.marker.x2 = x + 150;

      nextTick(() => {
        markerEditor.pointerUp({ x: x + 150, y: y } as IPoint);
      });
    }
  });
}

function textMarker(userClicked: boolean = true) {
  if (markerActionBlocked("text")) return;
  if (userClicked && currentEditorTypeName.value === LSTextMarker.typeName) {
    editor.value?.switchToSelectMode();
    currentEditorTypeName.value = null;
    currentSelectedMarker.value = undefined;
    return;
  }

  createMarkerWithoutCapturingToolSelection(LSTextMarker);
  const textMarkerEditor = editor.value?.currentMarkerEditor as
    | TextMarkerEditor
    | undefined;
  if (!textMarkerEditor) {
    return;
  }
  currentEditorTypeName.value = LSTextMarker.typeName;

  // Automatically add the marker in the center of the image after selecting it from the editor menu

  nextTick(() => {
    if (annotationImage.value) {
      const [width, height] = calculateImageDimensions(
        annotationImage.value.naturalWidth,
        annotationImage.value.naturalHeight,
      );
      // Same reasoning as autoPlaceLinearMarker above: name the caller, or an unmeasurable fit box
      // silently drops a text marker off-canvas (LSP-13917 review).
      if (!isUsableSize(width) || !isUsableSize(height)) {
        console.warn(
          "ImageEditor: text marker auto-placement got an unusable fitted size — placing the marker would put it off-canvas",
          {
            conversationId: props.conversationId,
            width,
            height,
            naturalWidth: annotationImage.value.naturalWidth,
            naturalHeight: annotationImage.value.naturalHeight,
          },
        );
        return;
      }
      let x = width / 2 - 125;
      let y = height / 2 - textMarkerEditor.marker.height * 2;
      textMarkerEditor.pointerDown({ x: x, y: y } as IPoint);

      // Deselect on next tick in order to render properly

      nextTick(() => {
        textMarkerEditor.pointerUp({ x: x, y: y } as IPoint);
        textMarkerEditor.deselect();
      });
    }
  });

  setTextMarkerStyle(textMarkerEditor);
  setMarkerAttributes();
}

function setTextMarkerStyle(textMarkerEditor: TextMarkerEditor) {
  textMarkerEditor.fontSize = {
    value: 24,
    units: "px",
    step: 1,
  } as FontSize;

  textMarkerEditor.marker.fillColor = "#ffffffaa";
  textMarkerEditor.marker.container.setAttribute("rx", "8");
  textMarkerEditor.marker.textBlock.wordWrap = true;
  textMarkerEditor.marker.padding = 8;
  textMarkerEditor.marker.adjustVisual();
}

function rectangleMarker(userClicked: boolean = true) {
  if (markerActionBlocked("rectangle")) return;
  if (userClicked && currentEditorTypeName.value === FrameMarker.typeName) {
    editor.value?.switchToSelectMode();
    currentEditorTypeName.value = null;
    currentSelectedMarker.value = undefined;
    return;
  }

  createMarkerWithoutCapturingToolSelection(FrameMarker);
  currentEditorTypeName.value = FrameMarker.typeName;
  setMarkerAttributes();
}

function ellipseMarker(userClicked: boolean = true) {
  if (markerActionBlocked("ellipse")) return;
  if (userClicked && currentEditorTypeName.value === EllipseMarker.typeName) {
    editor.value?.switchToSelectMode();
    currentEditorTypeName.value = null;
    currentSelectedMarker.value = undefined;
    return;
  }

  createMarkerWithoutCapturingToolSelection(EllipseMarker);
  currentEditorTypeName.value = EllipseMarker.typeName;
  setMarkerAttributes(false);
}

/**
 * Sets the style for the TextMarker when it's created/updated
 * @param marker The text marker to update
 * @param markerEditor The editor associated with the text marker
 * @param creating Determines if the marker is being created, if not, it is being updated
 */
function updateTextMarker(
  marker: TextMarker,
  markerEditor: TextMarkerEditor,
  creating = false,
) {
  lastTextMarker.value = marker;
  const markerArea = editor.value;
  const targetHeight = markerArea?.targetHeight;
  if (!markerArea || !isUsableSize(targetHeight)) return;

  const markerCanvas = markerArea.shadowRoot?.querySelector<SVGSVGElement>(
    ".canvas-container svg",
  );
  const actionBar = bottomControls.value;
  const actionGap = 16;
  const maximumBottom =
    markerCanvas && actionBar
      ? getUsableTextMarkerBottom(
          markerCanvas.getBoundingClientRect(),
          actionBar.getBoundingClientRect(),
          targetHeight,
          actionGap,
        )
      : Math.max(0, targetHeight - 96);

  if (markerEditor instanceof LSTextMarkerEditor) {
    markerEditor.setMaximumMarkerBottom(maximumBottom);
  } else {
    keepTextMarkerWithinTarget(marker, maximumBottom);
  }
}

function repositionMarker(marker: MarkerBase, markerEditor: MarkerBaseEditor) {
  editor.value?.editors.forEach((editor) => {
    const box = editor.marker.getBBox();
    let x: number = box.x;
    let y: number = box.y;

    if (editor.marker instanceof LinearMarkerBase) {
      x = editor.marker.x1;
      y = editor.marker.y1;
    }

    if (editor != markerEditor) {
      if (
        marker instanceof TextMarker &&
        x == marker.getBBox().x &&
        y == marker.getBBox().y
      ) {
        marker.left += 16;
        marker.top += 16;
      } else if (marker instanceof LinearMarkerBase) {
        const markerX = marker.x1;
        const markerY = marker.y1;

        if (x == markerX && y == markerY) {
          marker.x1 += 16;
          marker.x2 += 16;
          marker.y1 += 16;
          marker.y2 += 16;
          marker.adjustVisual();
        }
      }
    }
  });
}

/**
 * Initializes the Cropper. If the image has any annotations, a new image will be rendered with the annotations and set on the Cropper instance.
 * The rendered annotations are captured into the image-operation history first (see
 * captureCurrentImageState()/updateTargetImage()), so Undo can still step back to them — see undo().
 */
async function initializeCropper(
  userInitiated: boolean = false,
): Promise<void> {
  // isSaving: a non-crop save() render() must not run concurrently with this one either — save()
  // already blocks on cropperInitializing, this is the symmetric direction.
  if (
    isSaving.value ||
    props.persistencePending ||
    cropperInitializing.value ||
    annotationRestorePending.value
  ) {
    exitOrphanedCropMode();
    warnCropGuardSkipped("initializeCropper (Crop)");
    return;
  }

  if (userInitiated && cropping.value && cropper.value) {
    return;
  }

  const shadowRoot = editor.value?.shadowRoot;

  if (shadowRoot) {
    const editorAtStart = editor.value;
    const epochAtStart = cropSessionEpoch.value;
    renderLockOwner.value = editorAtStart;

    try {
      let src: string | undefined;
      const state = currentState.value;

      if (state && state.markers.length > 0) {
        src = await render(false, state);

        // A teardown/discard/retake could complete while the render above was in flight, clearing
        // or replacing editor.value (and/or ending the session via isEditing) — don't resurrect
        // crop mode on top of a session that has already ended, and don't attach it to a newly
        // re-opened editor instance either. cropSessionEpoch additionally catches a confirmed
        // Cancel/Reset that left editor.value/isEditing untouched (the broader editing session
        // stayed open) but specifically invalidated the crop this call was building.
        if (
          !editor.value ||
          editor.value !== editorAtStart ||
          !props.isEditing ||
          cropSessionEpoch.value !== epochAtStart
        ) {
          console.warn(
            "ImageEditor: initializeCropper's render() finished but the crop session was invalidated while it was in flight — discarding",
            {
              conversationId: props.conversationId,
              editorTornDown: !editor.value,
              editorReplaced: !!editor.value && editor.value !== editorAtStart,
              isEditing: props.isEditing,
              cropCancelled: cropSessionEpoch.value !== epochAtStart,
            },
          );
          return;
        }
      } else {
        src = currentImageUrl.value;
      }

      if (src && annotationImage.value) {
        clearCropper(true, userInitiated);
        cropping.value = true;

        // clearCropper() only clears the dirty flag on a fresh entry. The orientation-rebuild
        // call site (userInitiated defaults to false) must leave it alone, or a Cancel/Reset right
        // after rotating mid-adjustment would silently skip the confirm.

        try {
          const copy = document.createElement("img");
          copy.src = src;
          copy.crossOrigin = "anonymous";

          // Captured once the Cropper instance below is actually constructed (assigned
          // synchronously, right after this) — lets copy.onload tell whether it's still the
          // active cropper by the time the image decode resolves, not just whether SOME cropper
          // currently exists. Without this, a second initializeCropper() call landing (e.g. an
          // orientation rebuild) while this decode is still pending would create cropperInstance2,
          // and this stale onload would read cropper.value as truthy (it now IS cropperInstance2)
          // and mutate the WRONG instance's selection — spuriously flipping cropSelectionChanged.
          let cropperInstanceForThisLoad: Cropper | undefined;

          copy.onload = () => {
            // This callback can fire asynchronously, well after initializeCropper()'s own sync
            // try/catch has already returned — an exception here (e.g. the selection/image becoming
            // unavailable after a teardown raced this load) would otherwise escape as an uncaught
            // callback exception with no logging.
            try {
              const cropperInstance = cropper.value;
              if (
                !cropperInstance ||
                cropperInstance !== cropperInstanceForThisLoad
              ) {
                console.warn("ImageEditor: ignoring a stale crop image load", {
                  conversationId: props.conversationId,
                  activeCropperExists: Boolean(cropperInstance),
                });
                return;
              }
              const selection = cropperInstance.getCropperSelection();
              const cropperImage = cropperInstance.getCropperImage();

              if (selection && cropperImage) {
                // Lock the aspect ratio

                const rect = cropperImage.getBoundingClientRect();

                selection.$clear();
                selection.aspectRatio =
                  cropperImage.$image.width / cropperImage.$image.height;
                selection.initialAspectRatio = selection.aspectRatio;
                selection.width = rect.width / 2;
                selection.height = rect.height / 2;

                const cropperX =
                  rect.left + rect.width / 2 - selection.width / 2;
                const cropperY =
                  rect.top + rect.height / 2 - selection.height / 2;

                selection.$moveTo(cropperX, cropperY);

                nextTick(() => {
                  selection.onchange = onCropperSelectionChange;
                });

                // Override the zoom function to disable zooming on the image and always return the current one
                cropperImage.$zoom = (
                  scale: number,
                  x: number,
                  y: number,
                ): CropperImage => {
                  return cropperImage;
                };

                selection.zoomable = false;
              }

              if (mobile.value) {
                if (cropperImage) {
                  cropperImage.initialCenterSize = "contain";
                  cropperImage.rotatable = false;
                }
              }
            } catch (err) {
              console.error(err, {
                tags: {
                  component: "ImageEditor",
                  method: "initializeCropper (copy.onload)",
                  conversationId: props.conversationId,
                },
              });
              if (exitCropModeIfOwned(editorAtStart, epochAtStart)) {
                const message = "Failed to enter crop mode";
                const timeout = Math.min(
                  Math.max(message.length * 50, 3200),
                  7000,
                );
                toast.error(message, { timeout });
              }
            }
          };

          cropper.value = new Cropper(copy, {
            container: document.getElementById(
              "crop-container",
            ) as HTMLDivElement,
          });
          cropperInstanceForThisLoad = cropper.value;

          // Immediately disable the canvas background and shade to prevent a flash of the
          // default CropperJS checkerboard pattern before the image loads
          const canvas = cropper.value.getCropperCanvas();

          if (canvas) {
            canvas.background = false;
          }

          const shade = cropper.value.container.querySelector(
            "cropper-shade",
          ) as HTMLElement;

          if (shade) {
            shade.style.display = "none";
          }
        } catch (err) {
          // The ownership-aware outer catch below performs the one cleanup for both a failed
          // render and a failed Cropper construction. Keeping it there prevents a stale attempt
          // from tearing down a newer crop session and avoids double-bumping the session epoch.
          throw err;
        }
      }
    } catch (err) {
      console.error(err, {
        tags: {
          component: "ImageEditor",
          method: "initializeCropper",
          conversationId: props.conversationId,
        },
      });
      // A rejected render() can happen during an orientation rebuild, after clearCropper(false)
      // deliberately left `cropping` true while removing its Cropper. Only the attempt that still
      // owns that empty state may exit it: an older rejected render must never erase a newer crop
      // session that replaced this editor or advanced the crop epoch.
      if (exitCropModeIfOwned(editorAtStart, epochAtStart)) {
        const message = "Failed to enter crop mode";
        const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
        toast.error(message, { timeout });
      }
    } finally {
      // Only release the lock if we still own it — a newer call (for a different, fresher
      // session) may have already claimed it while this stale one was still in flight; releasing
      // unconditionally would clobber that newer call's own in-flight state.
      if (renderLockOwner.value === editorAtStart) {
        renderLockOwner.value = null;
      }
    }
  } else {
    // This can occur during an orientation rebuild after clearCropper(false): keep the crop UI
    // visible only while a replacement Cropper can actually be created. A missing shadow root has
    // no viable retry path, so exit the orphaned mode rather than leaving Cancel/Reset inert.
    exitOrphanedCropMode();
    console.warn(
      "ImageEditor: initializeCropper skipped — editor is not yet initialized (image still decoding)",
      {
        conversationId: props.conversationId,
        isEditing: props.isEditing,
      },
    );
  }
}

function exitCropModeIfOwned(
  editorAtStart: MarkerArea | null,
  epochAtStart: number,
): boolean {
  if (
    editor.value !== editorAtStart ||
    cropSessionEpoch.value !== epochAtStart ||
    !cropping.value
  ) {
    return false;
  }

  // `new Cropper()` can succeed and assign cropper.value before its own follow-up setup
  // (getCropperCanvas()/shade access) throws. This attempt still owns the editor + epoch, so
  // clear that half-built instance too; only the no-instance path is truly orphaned.
  if (cropper.value) {
    clearCropper();
  } else {
    exitOrphanedCropMode();
  }
  return true;
}

function exitOrphanedCropMode() {
  if (!cropping.value || cropper.value) {
    return;
  }

  cropping.value = false;
  cropSelectionChanged.value = false;
  cropSessionEpoch.value++;
  cropContainer.value?.replaceChildren();
}

/**
 * Clear the Cropper
 * @param hideCropper Determines if the container should continue to show. This is used during orientation changes so that
 * the container does not disappear and re-appear, revealing the editor. Makes for a smoother transition.
 * @param resetCropSelectionChanged Determines whether clearing this Cropper ends the user's pending crop adjustment.
 */
function clearCropper(
  hideCropper: boolean = true,
  resetCropSelectionChanged: boolean = hideCropper,
) {
  if (cropper.value) {
    cropper.value = undefined;
    cropSessionEpoch.value++;
    cropContainer.value?.replaceChildren();
  }

  // An orientation rebuild deliberately calls clearCropper(false), leaving crop mode visible while
  // its replacement is constructed. If a parent teardown arrives in that gap, the second,
  // default clearCropper() call must still end crop mode even though the first call already removed
  // cropper.value; otherwise the editor can close with `cropping === true` forever.
  if (hideCropper) {
    cropping.value = false;
  }
  if (resetCropSelectionChanged) {
    cropSelectionChanged.value = false;
  }
}

/**
 * Saves the cropped photo and updates the Base64 imageUrl that will be used to re-initialize the editor
 */
async function saveCrop(): Promise<void> {
  if (!cropper.value) {
    // An orientation/resize rebuild can briefly leave crop mode visible before its replacement
    // Cropper is ready. Keep the editor unchanged and leave a diagnostic instead of making Apply
    // appear to do nothing.
    warnCropGuardSkipped("saveCrop (cropper unavailable)");
    return;
  }

  // Guards against a double-click on Apply re-entering saveCrop() while the first canvas
  // conversion is still pending — unlike reset()/discard()/retake(), this had no guard at all.
  // Also guards against Apply racing a pending Cancel/Reset confirm dialog (confirmingCropDiscard):
  // without this, Apply could finish and exit crop mode while that dialog is still open and
  // unanswered, so the user's eventual answer would act on state that already changed underneath it.
  if (
    isSaving.value ||
    props.persistencePending ||
    annotationRestorePending.value ||
    confirmingCropDiscard.value
  ) {
    warnCropGuardSkipped("saveCrop (Apply)");
    return;
  }

  isSaving.value = true;

  try {
    const cropperCanvas = cropper.value.getCropperSelection();
    const canvas = await cropperCanvas?.$toCanvas();
    const url: string | undefined = canvas?.toDataURL();

    // ls-heroku-ci (LSP-15699, finding #3): a zero-area crop selection (CropperJS enforces no
    // minimum selection size) makes toDataURL() return the non-image string "data:," — truthy,
    // so it would otherwise fall into the success path, push a history entry, and only THEN fail
    // updateTargetImage()'s startsWith("data:image") check — orphaning that entry for a crop that
    // never actually applied. Routed into the existing "canvas could not be created" failure path
    // instead, before anything gets pushed.
    if (url && url.startsWith("data:image")) {
      // ls-heroku-ci (LSP-15699, round 2 finding #3): dropped the `!cropSelectionChanged.value`
      // half of this condition. cropperselectionchange sets cropSelectionChanged unconditionally
      // on every drag, including one that settles back on the exact original region — so a
      // same-bytes crop could still fail this check and fall into updateTargetImage(url) below with
      // url === currentImageUrl.value, hitting the same same-src-reassignment hazard undo() had
      // (image.src reassigned to the value it already holds doesn't reliably refire "load" in
      // Chromium/WebKit, leaving the editor permanently null). Comparing the resulting bytes is the
      // correct check regardless of whether the selection visually moved; clearCropper() below
      // already resets cropSelectionChanged.value.
      if (url === currentImageUrl.value) {
        console.warn(
          "ImageEditor: saveCrop skipped — crop raster matches the current image",
          {
            conversationId: props.conversationId,
          },
        );
        clearCropper();
        return;
      }

      // clearCropper()/cropSelectionChanged run BEFORE updateTargetImage() on purpose:
      // updateTargetImage() calls clear(), which unconditionally resets isSaving to false — if that
      // ran first, the flush watcher could fire mid-Apply (isSaving momentarily false, cropping.value
      // still true) and flush a deferred resize/teardown out from under this still-finishing save
      // (Cursor Bugbot). Finishing the crop-exit bookkeeping first closes that specific race, but
      // isSaving clearing here does NOT mean the image is done decoding — updateTargetImage()'s
      // reassigned .src decodes asynchronously well after this function returns, so a resize/
      // orientation refresh landing in that window is guarded separately via annotationImageLoaded
      // (see onOrientationChange/onWindowResize and the pendingLayoutRefresh flush above).
      clearCropper();
      cropSelectionChanged.value = false;
      const targetImageUpdate = updateTargetImage(url);
      // updateTargetImage() owns the next editor session and synchronously advances its epoch
      // before returning this promise. A deferred parent swap or teardown can supersede that
      // session while the await yields, so only its still-current session may retain this crop's
      // dirty state afterwards.
      const cropSaveSessionEpoch = editingSessionEpoch.value;
      await targetImageUpdate;
      // updateTargetImage() starts a new editor session and clears the flattened annotation state.
      // Keep the crop's outer-save dirty signal only when that new session is still current.
      if (
        props.isEditing &&
        editingSessionEpoch.value === cropSaveSessionEpoch
      ) {
        isDirty.value = true;
      } else {
        console.warn(
          "ImageEditor: saveCrop skipped — target image update was superseded",
          {
            conversationId: props.conversationId,
            isEditing: props.isEditing,
            cropSaveSessionEpoch,
            editingSessionEpoch: editingSessionEpoch.value,
          },
        );
      }
    } else {
      console.warn(
        "ImageEditor: saveCrop skipped — crop canvas could not be created",
        {
          conversationId: props.conversationId,
        },
      );
      const message = "Unable to apply your crop";
      const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
      toast.error(message, { timeout });
    }
  } catch (err) {
    console.error(err, {
      tags: {
        component: "ImageEditor",
        method: "saveCrop",
        conversationId: props.conversationId,
      },
    });
    const message = "Failed to save your crop";
    const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
    toast.error(message, { timeout });
  } finally {
    isSaving.value = false;
  }
}

/**
 * Sets the color and stroke of the current marker or currently selected markers
 * @param setFillColor Parameter that determines if the fill color should be set to the currently selected color, otherwise the color will be transparent
 */
function setMarkerAttributes(
  setFillColor: boolean = true,
) {
  if (markerActionBlocked("set marker attributes")) return;
  const editorAtRecolor = editor.value;
  if (!editorAtRecolor) return;

  const selectedMarkerEditors = [...editorAtRecolor.selectedMarkerEditors];
  const currentEditor = editorAtRecolor.currentMarkerEditor;
  if (
    selectedMarkerEditors.length === 0 ||
    (selectedMarkerEditors.length === 1 &&
      selectedMarkerEditors[0] === currentEditor)
  ) {
    const shouldCapture = Boolean(
      currentEditor &&
      currentEditor.state !== "new" &&
      currentEditor.state !== "creating",
    );
    const stateBeforeRecolor = shouldCapture
      ? currentState.value ?? editorAtRecolor.getState()
      : null;

    if (currentEditor) {
      markerStateCaptureBlocked = editorAtRecolor;
      try {
        setMarkerColor(currentEditor, setFillColor);
      } finally {
        markerStateCaptureBlocked = null;
      }
    }
    if (stateBeforeRecolor) {
      currentState.value ??= stateBeforeRecolor;
      captureCurrentImageState(editorAtRecolor);
    }
    return;
  }

  const selectedMarkerIndexes = new Set(
    editorAtRecolor.editors
      .map((markerEditor, index) =>
        selectedMarkerEditors.includes(markerEditor) ? index : -1,
      )
      .filter((index) => index >= 0),
  );
  const stateBeforeRecolor =
    currentState.value ?? editorAtRecolor.getState();
  const allLiveMarkersSelected =
    editorAtRecolor.editors.length > 0 &&
    editorAtRecolor.editors.every((markerEditor) =>
      selectedMarkerEditors.includes(markerEditor),
    );

  selectedMarkerEditors.forEach((markerEditor) => {
    setMarkerColor(markerEditor, setFillColor);
  });

  nextTick(() => {
    if (editor.value !== editorAtRecolor) {
      currentEditorTypeName.value = null;
      currentSelectedMarker.value = undefined;
      console.warn(
        "ImageEditor: bulk marker recolor skipped — editor was replaced before the update completed",
        { conversationId: props.conversationId },
      );
      return;
    }

    const liveStateAfterRecolor = editorAtRecolor.getState();
    const preserveMissingTrackedMarkers =
      allLiveMarkersSelected &&
      liveStateAfterRecolor.markers.length < stateBeforeRecolor.markers.length &&
      liveStateAfterRecolor.width === stateBeforeRecolor.width &&
      liveStateAfterRecolor.height === stateBeforeRecolor.height;
    const recolorTrackedMarker = (
      markerState: AnnotationState["markers"][number],
    ) => {
      const recoloredMarker = { ...markerState } as typeof markerState & {
        color?: string;
        fillColor?: string;
        strokeColor?: string;
        strokeWidth?: number;
      };
      const color = annotationColor.value === "" ? "#000" : annotationColor.value;

      if (recoloredMarker.typeName === LSTextMarker.typeName) {
        recoloredMarker.color = color;
        return recoloredMarker;
      }

      if ("strokeWidth" in recoloredMarker) recoloredMarker.strokeWidth = 8;
      if ("strokeColor" in recoloredMarker) {
        recoloredMarker.strokeColor = color;
      }
      if ("fillColor" in recoloredMarker) {
        recoloredMarker.fillColor =
          recoloredMarker.typeName === DotMarker.typeName && setFillColor
            ? color
            : "transparent";
      }
      return recoloredMarker;
    };
    const recoloredState = preserveMissingTrackedMarkers
      ? {
          ...liveStateAfterRecolor,
          markers: stateBeforeRecolor.markers.map(recolorTrackedMarker),
        }
      : liveStateAfterRecolor;

    captureCurrentImageState(editorAtRecolor, recoloredState);
    if (!currentState.value) return;

    clear(false, false);
    const recolorRestored = initializeEditor(
      "bulk marker recolor reinitialization",
      true,
      {
        onSuccess: () => {
          const restoredEditor = editor.value;
          if (!restoredEditor) return;

          restoredEditor.editors.forEach((markerEditor, index) => {
            if (
              preserveMissingTrackedMarkers ||
              selectedMarkerIndexes.has(index)
            ) {
              restoredEditor.selectEditor(markerEditor);
            }
          });
        },
      },
    );
    if (recolorRestored) setDimensions();
  });
}

/**
 * Sets the color and stroke of the current marker
 * @param setFillColor Parameter that determines if the fill color should be set to the currently selected color, otherwise the color will be transparent
 */
function setMarkerColor(
  markerEditor: MarkerBaseEditor,
  setFillColor: boolean = true,
) {
  if (markerEditor) {
    const color = annotationColor.value == "" ? "#000" : annotationColor.value;

    if (markerEditor instanceof TextMarkerEditor) {
      (markerEditor as TextMarkerEditor).color = color;
      return;
    }

    markerEditor.strokeWidth = 8;
    markerEditor.strokeColor = color;

    if (
      markerEditor instanceof ShapeMarkerEditor &&
      !(markerEditor instanceof DotMarkerEditor)
    ) {
      setFillColor = false;
    }

    if (setFillColor) {
      markerEditor.fillColor = color;
    } else {
      markerEditor.fillColor = "transparent";
    }
  }
}

/**
 * Event handler that handles the movement of the cropper selection and prevents it from going outside of the image bounds
 * @param event The event from the Cropper instance
 */
function onCropperSelectionChange(event: Event) {
  if (clampingCropSelection) {
    return;
  }
  const evt = event as CustomEvent;
  const selection = evt.detail as Selection;

  const cropperImage = cropper.value?.getCropperImage();
  const cropperCanvas = cropper.value?.getCropperCanvas();

  if (cropperImage && cropperCanvas) {
    const cropperCanvasRect = cropperCanvas.getBoundingClientRect();
    const cropperImageRect = cropperImage.getBoundingClientRect();
    const maxSelection: Selection = {
      x: cropperImageRect.left - cropperCanvasRect.left,
      y: cropperImageRect.top - cropperCanvasRect.top,
      width: cropperImageRect.width,
      height: cropperImageRect.height,
    };

    if (!inBounds(selection, maxSelection)) {
      event.preventDefault();
      const cropperSelection = cropper.value?.getCropperSelection();
      if (cropperSelection) {
        const clampedX = Math.max(
          maxSelection.x,
          Math.min(
            selection.x,
            maxSelection.x + maxSelection.width - selection.width,
          ),
        );
        const clampedY = Math.max(
          maxSelection.y,
          Math.min(
            selection.y,
            maxSelection.y + maxSelection.height - selection.height,
          ),
        );
        const clampedWidth = Math.min(selection.width, maxSelection.width);
        const clampedHeight = Math.min(selection.height, maxSelection.height);
        clampingCropSelection = true;
        try {
          cropperSelection.$change(
            clampedX,
            clampedY,
            clampedWidth,
            clampedHeight,
          );
        } finally {
          clampingCropSelection = false;
        }
      }
      // The out-of-bounds selection came from a real user drag. Its corrected position is still
      // an unsaved crop change; only the synchronous $change re-entry above is programmatic.
      cropSelectionChanged.value = true;
      return;
    }
  }

  cropSelectionChanged.value = true;
}

/**
 * Checks to see if the current selection is within the bounds of the current image
 * @param selection The current selection
 * @param maxSelection The image bounds
 */
function inBounds(selection: Selection, maxSelection: Selection) {
  return (
    selection.x >= maxSelection.x &&
    selection.y >= maxSelection.y &&
    selection.x + selection.width <= maxSelection.x + maxSelection.width &&
    selection.y + selection.height <= maxSelection.y + maxSelection.height
  );
}

/**
 * The state every terminal teardown shares: reset(), discard(), retake() (each of which then emits
 * its own event) and tearDownForEditingClosed().
 *
 * Funnelled so a fifth teardown cannot be added that silently forgets one of these — the
 * pendingLayoutRefresh/cancelScheduledLayoutRefreshAndResetWindow pair was present only in
 * tearDownForEditingClosed, so a viewport change around a Discard/Retake could still rebuild an
 * empty MarkerArea over the closed editor (LSP-13917).
 *
 * Deliberately excludes showControls and wasShowingControls: every caller sets those differently
 * (retake hides the controls outright, discard and tearDownForEditingClosed restore the
 * defaultShowControls prop, reset leaves showControls alone) and both are written AFTER the caller's
 * own emit, so folding them in here would also move them across that emit boundary.
 */
function releaseEditingSessionState() {
  clear();
  imageOperationHistory.value = [];
  releaseImageObjectUrls();
  // ls-heroku-ci (follow-up): lastDecodedImage is never reset anywhere else — without this it
  // permanently pins a full retained image (with its own imageOperationHistory slice) past this
  // session's end, until the next successful decode overwrites it.
  lastDecodedImage.value = undefined;
  // releaseImageObjectUrls() above has just revoked/cleared this session's image, so nothing is
  // decoded any more. Left true, this flag tells a later layout refresh there is a live image to
  // measure against.
  annotationImageLoaded.value = false;
  // A queued or deferred viewport refresh belonging to the session that is ending would otherwise
  // rebuild an empty MarkerArea over an editor the user has already left, or survive into the NEXT
  // reopened session and rebuild that one for a refresh it never asked for.
  discardQueuedLayoutRefresh();
}

/**
 * Clears the editor and all states
 * @param clearState Determines if the editor state should be cleared. This is used during orientation changes to preserve the state of the editor
 */
function clear(clearState: boolean = true, invalidateSession: boolean = true) {
  if (invalidateSession) {
    editingSessionEpoch.value++;
  }
  if (showControls.value) {
    wasShowingControls.value = true;
  }

  container.value?.replaceChildren();
  cancelPendingAnnotationRestoreObservation();
  annotationRestoreOwner.value = null;
  editor.value = null;
  showControls.value = props.defaultShowControls ?? false;
  isSaving.value = false;
  currentEditorTypeName.value = null;
  currentSelectedMarker.value = undefined;

  if (clearState) {
    currentState.value = null;
    isDirty.value = false;
    cropSelectionChanged.value = false;
  }
}

/**
 * Saves the edited photo to be uploaded to the server or crops the photo (if in cropping mode)
 */
async function save(): Promise<void> {
  if (
    isSaving.value ||
    props.persistencePending ||
    annotationRestorePending.value
  ) {
    warnCropGuardSkipped("save (Apply)");
    return;
  }

  if (confirmingEditorDiscard.value) {
    console.warn(
      "ImageEditor: save skipped — discard confirmation is in flight",
      {
        conversationId: props.conversationId,
      },
    );
    return;
  }

  if (cropping.value) {
    await saveCrop();
    return;
  }

  // Guards against Save starting its own render() while a Crop click's initializeCropper() render()
  // is still in flight (cropperInitializing stays true for that whole window, before cropping.value
  // itself flips true) — without this, both would rasterize the same editor/state concurrently.
  if (cropperInitializing.value) {
    warnCropGuardSkipped("save (Apply)");
    return;
  }

  if (!editor.value) {
    console.warn(
      "ImageEditor: save skipped — editor is not yet initialized (image still decoding)",
      {
        conversationId: props.conversationId,
        isEditing: props.isEditing,
      },
    );
    toast.info("Your photo is still loading. Please try Save again in a moment.");
    return;
  }

  // LSP-17747: nothing was annotated, cropped or rotated, so there is nothing to render. Hand the
  // decision to the parent, which owns persistence and knows whether this photo is already stored —
  // rendering and re-emitting the untouched capture here would save a second copy of it.
  //
  // LSP-20011 removed the isAsyncApp condition that used to sit on this check. It was the other half
  // of saveDisabled's admin-only change-gate: with Save disabled in that state, contact-admin could
  // not reach an unchanged save from the UI, so the emit was async-only. Now that Save is clickable
  // in both apps, this is the ONLY thing standing between an unchanged click and a duplicate
  // annotated copy in contact-admin — so it must run for both consumers. contact-async's behaviour
  // is unchanged (it already took this branch); contact-admin answers the emit with a neutral
  // "No changes to save." and leaves the editor open, uploading nothing.
  //
  // ls-heroku-ci (LSP-20011): `hasChanges` cannot tell "the user annotated nothing" apart from "the
  // tracked marker collection is corrupt and isDirty happens to be false", and both now leave
  // through this branch rather than through render()'s throw. No user work is lost either way —
  // isDirty is what records that the user did something — but the corrupt case used to be the only
  // thing that logged, so it is logged explicitly here per CLAUDE.md -> "Diagnostic logging when
  // fixing silent failures". Only the malformed shape is logged; an ordinary unchanged Save is a
  // normal, frequent action in the async capture flow and must not spam the console.
  if (!hasChanges.value) {
    const trackedMarkers = currentState.value?.markers;
    if (trackedMarkers !== undefined && !Array.isArray(trackedMarkers)) {
      console.warn(
        "ImageEditor: save reported no changes while the tracked marker collection is not an array — treating it as unchanged rather than rendering it",
        {
          conversationId: props.conversationId,
          snapshotId: props.snapshotId,
          isAsyncApp: props.isAsyncApp,
          isDirty: isDirty.value,
          trackedMarkersType: trackedMarkers === null ? "null" : typeof trackedMarkers,
        },
      );
    }
    emits("saveUnchanged");
    return;
  }

  isSaving.value = true;

  try {
    const dataUrl = await render();
    if (!dataUrl) {
      throw new Error("Image editor did not produce a rendered image");
    }

    const blob = dataURLToBlob(dataUrl);
    if (!blob) {
      throw new Error("Image editor rendered an invalid image");
    }

    emits("imageSaved", blob, true, getSavePersistenceAttemptId());
    wasShowingControls.value = false;
    deselectMarker();
    // ls-heroku-ci (LSP-15699, round 3): emits() above is fire-and-forget — the parent's own
    // persistence is async and can still fail (see disconnectedHomePageMethods.saveSnapshot's catch,
    // which deliberately leaves the editor open on failure for the user to retry). Clearing either
    // isDirty or history here would make a marker-free structural edit unsaveable or wipe its Undo
    // stack on that failure path. tearDownForEditingClosed() clears both once the parent actually
    // closes the editor (isEditing -> false) after persistence succeeds.
  } catch (err) {
    console.error(err, {
      tags: {
        component: "ImageEditor",
        method: "save",
        conversationId: props.conversationId,
      },
    });
    const message = "Failed to save your edits";
    const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
    toast.error(message, { timeout });
  } finally {
    isSaving.value = false;
  }
}

function reportDiscardConfirmationFailure(resetCrop: boolean) {
  const message = resetCrop
    ? "Oops, we couldn't reset your picture. Please try again."
    : "Oops, we couldn't discard your changes. Please try again.";
  const timeout = Math.min(Math.max(message.length * 50, 3200), 7000);
  toast.error(message, { timeout });
}

/**
 * Renders the current image and annotations in the editor
 * @param final Determines if this will be the final rendering of the image. If not, PNG will be used to preserve the quality until the final image is rendered
 * @returns Base64 image data URL
 */
async function render(
  final: boolean = true,
  state: AnnotationState | null = null,
): Promise<string | undefined> {
  if (editor.value && currentImageUrl.value) {
    const renderer = new Renderer();
    // LSP-11415: never emit JPEG for the final render. iOS's JPEG *decoder* renders a green line
    // at the bottom of certain-sized photos, so an edited/annotated photo saved as JPEG on iOS
    // Safari reintroduces the exact bug the capture path fixes. WebP (final) and PNG (intermediate)
    // both dodge that decode path; toDataURL substitutes PNG for an unsupported type, so
    // WebP-incapable browsers still get a clean PNG (the data-URL prefix carries the real type).
    const mimeType = final ? "image/webp" : "image/png";

    renderer.registerMarkerType(DotMarker);
    renderer.registerMarkerType(LSTextMarker);
    renderer.naturalSize = true;
    renderer.imageType = mimeType;
    renderer.imageQuality = props.imageQuality ?? 1.0;
    renderer.markersOnly = false;

    const target = document.createElement("img");
    target.src = currentImageUrl.value;
    target.crossOrigin = "anonymous";

    renderer.targetImage = target;
    const rawState = state ?? editor.value.getState();
    const clonedState: AnnotationState = JSON.parse(JSON.stringify(rawState));
    adjustState(clonedState);
    const dataUrl = await renderer.rasterize(clonedState);
    return dataUrl;
  }

  return undefined;
}

/**
 * Adjusts the state of the editor to ensure that stroke widths are scaled consistently to the
 * natural (full-resolution) image size the annotation is rendered at.
 *
 * MarkerJS rescales the markers from the state's own coordinate space up to the natural image size,
 * and that rescale multiplies strokeWidth for every marker type used here EXCEPT freehand and text
 * markers — those two only get their geometry scaled (see markerjsStrokeScalingGuard.spec.ts), so
 * they are the only ones pre-compensated here. Touching any other type double-scales it.
 *
 * LSP-16400: the scale factor comes from state.width — the space the markers were drawn in
 * (editor.targetWidth, from calculateImageDimensions()) — and NOT from the annotation <img>
 * element's laid-out width. The element fills the editor container while the editor fits the image
 * inside the window, so for any image narrower than that container (e.g. a portrait screenshot on a
 * desktop window) the element-based factor was too small: saved freehand strokes came out thinner
 * than drawn, while the types MarkerJS rescales itself came out thicker. Each marker's own drawn
 * strokeWidth is scaled rather than replaced with the editor's default, so a state restored from
 * history or from another window size keeps the thickness it was drawn with.
 *
 * There is deliberately no separate mobile adjustment. Mobile freehand strokes used to be forced to
 * a flat 24px "because something internally shrinks the stroke" — that something was this very
 * missing scale factor, which is largest on a phone (a 4000px photo drawn in a ~400px editor). With
 * the factor applied, the extra 3x only made a saved mobile stroke three times thicker than its own
 * preview, which is the same preview/saved mismatch from the other direction.
 * @param state The state of the editor (must be a clone — this function mutates it)
 */
function adjustState(state: AnnotationState) {
  const naturalWidth = annotationImage.value?.naturalWidth;
  // MarkerJS only rescales marker geometry when both the state's and the natural image's dimensions
  // form a usable coordinate space, so a stroke may only be pre-scaled when they all do —
  // otherwise the stroke would be rescaled while the geometry it belongs to is not.
  const candidateScaleX =
    isUsableSize(naturalWidth) &&
    isUsableSize(annotationImage.value?.naturalHeight) &&
    isUsableSize(state.width) &&
    isUsableSize(state.height)
      ? naturalWidth / state.width
      : 1;
  // The ratio itself still has to be usable: a state width small enough to overflow it would
  // otherwise turn every stroke into an infinite width.
  const scaleX = isUsableSize(candidateScaleX) ? candidateScaleX : 1;

  for (const marker of state.markers) {
    const isFreehandMarker = marker.typeName === FreehandMarker.typeName;
    if (!isFreehandMarker && marker.typeName !== TextMarker.typeName) continue;
    // A missing or null strokeWidth is MarkerJS's signal to restore the marker's own default, so it
    // must stay as it is rather than become a number.
    if (!isUsableSize(marker.strokeWidth)) continue;

    const scaledStrokeWidth = marker.strokeWidth * scaleX;
    // Never trade a width MarkerJS can render for one it cannot: an extreme stored width can
    // overflow to Infinity or underflow to an invisible zero once scaled.
    if (isUsableSize(scaledStrokeWidth)) {
      marker.strokeWidth = scaledStrokeWidth;
    }
  }
}

/**
 * Resets the Cropper or editor.
 * @param reset Determines if the Cropper should be cleared and re-initialized with the original image
 */
async function reset(reset = true): Promise<void> {
  // Guards against Cancel/Reset racing an in-flight save: saveCrop() can finish and exit crop mode
  // while this function is still awaiting the user's answer to a confirm dialog it already opened
  // (leaving that dialog's eventual answer acting on stale state), and a non-crop save() holds
  // isSaving true for its entire duration while cropping.value is never true at all — #reset-image
  // is always visible regardless of cropping, so this must be unconditional, not scoped inside the
  // cropping branch below. cropperInitializing is checked too: cropping.value doesn't flip true
  // until after initializeCropper()'s render() await, so without this a Cancel/Reset here wouldn't
  // know a crop click is still pending and could emit discard/exit crop mode while that stale
  // continuation is still going to attach a Cropper afterward.
  if (
    isSaving.value ||
    props.persistencePending ||
    cropperInitializing.value ||
    (annotationRestorePending.value && cropping.value && reset)
  ) {
    warnCropGuardSkipped("reset (Cancel/Reset during a save)");
    return;
  }

  if (cropping.value) {
    // Guards against a rapid double-click on Cancel/Reset opening a second confirm dialog
    // (or taking any action at all) while the first is still awaiting a response. This check
    // must sit outside the cropSelectionChanged condition below — cropSelectionChanged can
    // read false on a second call (e.g. flipped by unrelated state) even while the first
    // call's confirm is still pending, which would otherwise let a re-entrant call skip the
    // dialog entirely and clear the crop out from under it.
    if (confirmingCropDiscard.value) {
      warnCropGuardSkipped("reset (re-entrant Cancel/Reset)");
      return;
    }

    if (!props.isAsyncApp && cropSelectionChanged.value) {
      confirmingCropDiscard.value = true;

      try {
        const root = getConfirmDialogRoot();
        if (!root) {
          console.warn(
            "ImageEditor: reset (Cancel/Reset while cropping) skipped — confirm dialog root unavailable",
            {
              conversationId: props.conversationId,
              reset,
            },
          );
          reportDiscardConfirmationFailure(reset);
          return;
        } else {
          const confirmCopy = reset
            ? {
                title: "Reset your crop?",
                message:
                  "This resets your crop area back to the default. Your current adjustments will be lost.",
                agreeText: "Reset",
                cancelText: "Keep Editing",
              }
            : {
                title: "Discard this crop?",
                message:
                  "Your crop selection hasn't been saved. Leaving crop mode will lose it.",
                agreeText: "Discard",
                cancelText: "Keep Cropping",
              };
          const sessionEpochAtConfirm = editingSessionEpoch.value;
          const result = await openDiscardChangesDialog(
            root.$confirm,
            confirmCopy,
          );

          if (!result) {
            return;
          }
          if (editingSessionEpoch.value !== sessionEpochAtConfirm) {
            console.warn(
              "ImageEditor: reset (Cancel/Reset while cropping) skipped — editor session changed while confirming crop reset",
              {
                conversationId: props.conversationId,
                reset,
              },
            );
            return;
          }
        }
      } catch (err) {
        // Unlike rotate()/initializeCropper()/saveCrop() (which all catch+log+toast their own risky
        // awaits), a rejected $confirm.open() here used to propagate uncaught — the finally below
        // still ran (confirmingCropDiscard cleared correctly), but the rejection itself escaped as an
        // unhandled promise rejection with no diagnostic trail.
        console.error(err, {
          tags: {
            component: "ImageEditor",
            method: "reset (Cancel/Reset while cropping confirm)",
            conversationId: props.conversationId,
          },
        });
        reportDiscardConfirmationFailure(reset);
        return;
      } finally {
        confirmingCropDiscard.value = false;
      }
    }

    if (reset) {
      const selection = cropper.value?.getCropperSelection();

      if (selection) {
        selection.$reset();
      }
    } else {
      clearCropper();
    }
    cropSelectionChanged.value = false;
  } else {
    if (!props.isAsyncApp) {
      if (hasUnsavedEditorWork()) {
        if (confirmingEditorDiscard.value) {
          console.warn(
            "ImageEditor: reset (Cancel/Reset) skipped — discard confirmation is already in flight",
            {
              conversationId: props.conversationId,
            },
          );
          return;
        }

        const root = getConfirmDialogRoot();
        if (!root) {
          console.warn(
            "ImageEditor: reset (Cancel/Reset) skipped — confirm dialog root unavailable",
            {
              conversationId: props.conversationId,
              reset,
            },
          );
          reportDiscardConfirmationFailure(false);
          return;
        }

        confirmingEditorDiscard.value = true;
        try {
          const sessionEpochAtConfirm = editingSessionEpoch.value;
          const result = await openEditorUnsavedChangesDialog(root.$confirm);

          if (result === null) {
            return;
          }
          if (editingSessionEpoch.value !== sessionEpochAtConfirm) {
            console.warn(
              "ImageEditor: reset (Cancel/Reset) skipped — editor session changed while confirming discard",
              {
                conversationId: props.conversationId,
              },
            );
            return;
          }
          if (result) {
            // Saving owns persistence and closing; never fall through to discard on failure.
            confirmingEditorDiscard.value = false;
            await save();
            return;
          }
        } catch (err) {
          console.error(err, {
            tags: {
              component: "ImageEditor",
              method: "reset (Cancel/Reset confirm)",
              conversationId: props.conversationId,
            },
          });
          reportDiscardConfirmationFailure(false);
          return;
        } finally {
          confirmingEditorDiscard.value = false;
        }
      }
    }

    // The confirmation above can be open while a programmatic save begins. Re-check before
    // emitting discard so a late confirmation response cannot tear down that active save.
    if (isSaving.value || props.persistencePending) {
      warnCropGuardSkipped("reset (Cancel/Reset, save began during confirm)");
      return;
    }

    const forceDiscard =
      (props.snapshotSaved ?? false) && !hasUnsavedEditorWork();
    // ls-heroku-ci (follow-up): this non-cropping Cancel/Reset path is a user-confirmed discard,
    // the same as discard()/retake()/tearDownForEditingClosed() — all three call clear() before
    // wiping history and emitting; this one was missing it, leaving markers/editor.value fully
    // intact right next to a now-empty (dead) Undo history.
    // contact-async owns the confirmation that follows this emitted request. Keep the live editor
    // intact until its parent accepts and closes the editing session; choosing "Keep Editing" must
    // return to the same annotations and Undo history.
    if (!props.isAsyncApp) {
      releaseEditingSessionState();
      wasShowingControls.value = false;
    }
    emits("discard", forceDiscard);
  }
}

/**
 * Discards the photo/current edits and emits the discard event.
 */
async function discard(): Promise<boolean> {
  // discard() is exposed for the parent to call directly (defineExpose), independent of any click
  // on this component's own buttons — so it must respect the same in-flight guard as saveCrop()/
  // save() no matter which one is running. isSaving stays true past saveCrop()'s own clearCropper()
  // call (which flips cropping.value to false before saveCrop()'s finally clears isSaving) and for
  // the entire duration of a non-crop save() (which never sets cropping.value true at all) — so this
  // check must be unconditional, not scoped inside the cropping branch below.
  // (No cropperInitializing check needed: the unconditional clear() below already nulls
  // editor.value regardless of path, which initializeCropper()'s post-await guard already treats
  // as a torn-down session.)
  if (
    isSaving.value ||
    props.persistencePending ||
    annotationRestorePending.value ||
    confirmingEditorDiscard.value
  ) {
    warnCropGuardSkipped("discard (parent-triggered)");
    return false;
  }

  // Guards against a parent-triggered discard tearing a crop down out from under a still-pending
  // Cancel/Reset confirm dialog. Keep an existing crop alive until this function's own annotation
  // discard confirmation is accepted; declining it must leave the crop untouched.
  if (cropping.value && confirmingCropDiscard.value) {
    warnCropGuardSkipped("discard (parent-triggered, confirm pending)");
    return false;
  }

  if (!props.isAsyncApp) {
    if (hasUnsavedEditorWork()) {
      const root = getConfirmDialogRoot();
      if (!root) {
        console.warn(
          "ImageEditor: discard (parent-triggered) skipped — confirm dialog root unavailable",
          {
            conversationId: props.conversationId,
          },
        );
        reportDiscardConfirmationFailure(false);
        return false;
      }

      confirmingEditorDiscard.value = true;
      try {
        const sessionEpochAtConfirm = editingSessionEpoch.value;
        const result = await openEditorUnsavedChangesDialog(root.$confirm);

        if (result === null) {
          return false;
        }
        if (editingSessionEpoch.value !== sessionEpochAtConfirm) {
          console.warn(
            "ImageEditor: discard (parent-triggered) skipped — editor session changed while confirming discard",
            {
              conversationId: props.conversationId,
            },
          );
          return false;
        }
        if (result) {
          confirmingEditorDiscard.value = false;
          await save();
          return false;
        }
      } catch (err) {
        console.error(err, {
          tags: {
            component: "ImageEditor",
            method: "discard (parent-triggered confirm)",
            conversationId: props.conversationId,
          },
        });
        reportDiscardConfirmationFailure(false);
        return false;
      } finally {
        confirmingEditorDiscard.value = false;
      }
    }
  }

  // A parent-triggered discard confirm can remain open while a new save begins. Its eventual
  // answer must not clear the editor underneath that save just because the first guard above ran
  // before the dialog was opened.
  if (
    isSaving.value ||
    props.persistencePending ||
    annotationRestorePending.value
  ) {
    warnCropGuardSkipped(
      "discard (parent-triggered, save began during confirm)",
    );
    return false;
  }

  const forceDiscard =
    (props.snapshotSaved ?? false) && !hasUnsavedEditorWork();

  // Re-check: a pending initializeCropper() call (started before this confirm dialog opened) could
  // have completed WHILE we were awaiting the user's answer above, entering crop mode after the
  // earlier cropping.value check already ran and found it false. Without this, clear() below would
  // null editor.value while leaving cropping/cropper stuck set to a now-orphaned crop attempt.
  if (cropping.value) {
    clearCropper();
    cropSelectionChanged.value = false;
  }

  releaseEditingSessionState();
  emits("discard", forceDiscard);
  showControls.value = props.defaultShowControls ?? false;
  wasShowingControls.value = false;
  return true;
}

/**
 * Clears the editor and emits retake for live-photo flows (contact-async).
 */
type RetakeResult =
  | true
  | "save-in-progress"
  | "crop-discard-confirmation-pending";

function retake(): RetakeResult {
  // Same reasoning as discard() above — unconditional, not scoped inside the cropping branch.
  // (retake()'s own clear() call below already nulls editor.value unconditionally, which
  // initializeCropper()'s post-await guard already treats as a torn-down session — no
  // cropperInitializing check needed here the way reset()/discard() need one.) A deferred
  // annotation restore is also safe to invalidate here: contact-async calls this exposed method
  // only after the user accepts its Retake confirmation, making this a terminal session teardown.
  if (isSaving.value || props.persistencePending) {
    warnCropGuardSkipped("retake (parent-triggered, save in flight)");
    return "save-in-progress";
  }

  if (cropping.value) {
    if (confirmingCropDiscard.value) {
      warnCropGuardSkipped("retake (parent-triggered, confirm pending)");
      return "crop-discard-confirmation-pending";
    }

    clearCropper();
    cropSelectionChanged.value = false;
  }

  releaseEditingSessionState();
  emits("retake");
  showControls.value = false;
  wasShowingControls.value = false;
  return true;
}

/**
 * Converts a Base64 data URL to a blob
 * @param dataUrl The data URL to convert
 * @returns A blob representation of the data URL
 */
function dataURLToBlob(dataUrl: string): Blob | null {
  const separatorIndex = dataUrl.indexOf(",");
  const header = dataUrl.slice(0, separatorIndex);
  const base64 = dataUrl.slice(separatorIndex + 1);

  if (separatorIndex < 0 || !header || !base64) {
    console.error(
      "ImageEditor: dataURLToBlob failed — missing data URL header or payload",
      {
        tags: {
          component: "ImageEditor",
          method: "dataURLToBlob",
          conversationId: props.conversationId,
        },
      },
    );
    return null;
  }

  const mime = header.match(/:(.*?);/)?.[1];
  if (!mime) {
    console.error(
      "ImageEditor: dataURLToBlob failed — data URL MIME type is missing",
      {
        tags: {
          component: "ImageEditor",
          method: "dataURLToBlob",
          conversationId: props.conversationId,
        },
      },
    );
    return null;
  }

  if (base64.includes(",")) {
    console.error(
      "ImageEditor: dataURLToBlob failed — base64 payload is invalid",
      {
        tags: {
          component: "ImageEditor",
          method: "dataURLToBlob",
          conversationId: props.conversationId,
        },
      },
    );
    return null;
  }

  try {
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);
    for (let byteIndex = 0; byteIndex < binary.length; byteIndex++) {
      array[byteIndex] = binary.charCodeAt(byteIndex);
    }
    return new Blob([array], { type: mime });
  } catch (err) {
    // A malformed data URL must not make a target-image replacement fail after its network
    // conversion has already succeeded. Keep the URL as the compatibility fallback; only valid
    // raster data is promoted to Blob-backed structural history.
    console.error(
      "ImageEditor: dataURLToBlob failed — base64 payload is invalid",
      {
        tags: {
          component: "ImageEditor",
          method: "dataURLToBlob",
          conversationId: props.conversationId,
        },
        extra: {
          error: err,
        },
      },
    );
    return null;
  }
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });
}

// Shared by the isEditing watcher's immediate and deferred-flush paths below. clear() alone leaves
// cropping/cropSelectionChanged untouched, which would let a later isEditing=true re-open the
// editor still believing it's mid-crop with unsaved changes from a session that already ended.
function tearDownForEditingClosed() {
  savePersistenceAttemptId = undefined;
  releaseEditingSessionState();
  annotationColor.value = "#000";
  showControls.value = props.defaultShowControls ?? false;
  wasShowingControls.value = false;
  // clearCropper() (not just resetting cropping.value directly) so a mid-crop session close also
  // nulls cropper.value and removes its DOM nodes/onchange handler — otherwise a live Cropper
  // instance stays attached while the UI thinks crop mode has ended.
  clearCropper();
  cropping.value = false;
  cropSelectionChanged.value = false;
}

watch(
  () => props.targetImage,
  async (newImage) => {
    if (!newImage) return;

    // A parent-driven image swap mid-Apply/mid-confirm would otherwise call updateTargetImage() ->
    // clear() out from under the in-flight operation (clear() unconditionally resets isSaving and
    // tears down the editor instance saveCrop() is still holding a reference to). Defer to the
    // pendingTargetImage flush below instead of applying it immediately.
    if (
      isSaving.value ||
      confirmingCropDiscard.value ||
      confirmingEditorDiscard.value
    ) {
      warnCropGuardSkipped("targetImage prop update");
      pendingTargetImage.value = newImage;
      return;
    }

    try {
      // A parent image selection identifies a different artifact. If it cannot
      // be decoded, never restore the prior image into this new session: Save
      // could otherwise upload photo A under photo B's artifact identity.
      await updateTargetImage(newImage, true, false, true);
    } catch (err) {
      reportTargetImageFailure(err, "targetImage prop update");
    }
  },
);

watch(
  () => props.isEditing,
  async (isEditing, wasEditing) => {
    if (!isEditing && wasEditing) {
      // Invalidate any confirmation already open before a guarded/deferred teardown. The parent can
      // reopen editing before that teardown flushes, so relying on clear() alone would let the old
      // dialog mutate the newly reopened session.
      editingSessionEpoch.value++;
      // Same reasoning as the targetImage watcher above — a parent-driven isEditing=false mid-Apply
      // would otherwise call clear() out from under saveCrop()/reset(). Defer to the
      // pendingEditingTeardown flush below instead of tearing the editor down immediately.
      if (isSaving.value || confirmingCropDiscard.value) {
        warnCropGuardSkipped("isEditing teardown");
        pendingEditingTeardown.value = true;
        return;
      }

      tearDownForEditingClosed();
      return;
    }

    if (!isEditing || wasEditing) {
      return;
    }

    // LSP-18576, and it MUST stay synchronous and ahead of the await below: a parent can MOUNT this
    // component already editing — MediaLightBox's Cancel nulls the URL in the editor's own v-if, so
    // the next Annotate click unmounts and immediately remounts with isEditing already true. This
    // watcher is { immediate: true }, so that case runs during setup, where helperCanvas does not
    // exist yet (onMounted creates it) and annotationImage is still null. Loading here would reject
    // inside imageToDataUrl with "Failed to get canvas" and toast "Failed to load image" at the
    // user, and even a data: URL that skips that conversion could not reach the missing
    // annotationImage element. onMounted already performs exactly this load, with both available,
    // and its onload handler initializes the editor because props.isEditing is true by then — so
    // pre-mount, do nothing. Awaiting first would resume this run AFTER onMounted had set
    // hasMounted, so the guard would stop firing and we would double-load alongside onMounted.
    if (!hasMounted) return;

    // Everything below MEASURES the overlay, so it must not run until the overlay is actually
    // visible (LSP-13917).
    //
    // This watcher's own trigger, props.isEditing, is the value driving `v-show="isEditing"` on
    // #editor-container. Vue's vShow directive writes `style.display` from its `updated` hook, which
    // is queued into the post-render queue DURING the patch — i.e. AFTER this watcher's job was
    // queued. So neither a default pre-flush watcher NOR flush: "post" observes the applied style:
    // both still read display:none, clientWidth/clientHeight 0, which makes getEditorFitBox() skip
    // its most-trustworthy tier and fall through to document.documentElement. In MediaLightBox the
    // overlay is `position: absolute; width: 100%; height: 100%` of a NON-fullscreen host, so that
    // fallback is a different wrong answer than the one this ticket set out to fix, and nothing
    // re-measures afterwards. flush: "post" was measured and rejected for exactly this reason.
    //
    // One awaited nextTick yields past the WHOLE post-render queue, vShow's hook included, so the
    // overlay is genuinely laid out and measurable from here on.
    const epochBeforeOverlayFlush = editingSessionEpoch.value;
    await nextTick();
    // The await is a re-entrancy window: the parent can close (or tear down and reopen) editing
    // while we are yielding, and measuring then would size a session that no longer exists.
    if (!props.isEditing || editingSessionEpoch.value !== epochBeforeOverlayFlush) {
      console.warn(
        "ImageEditor: isEditing reopen abandoned — the session changed while waiting for the overlay to become visible",
        {
          conversationId: props.conversationId,
          isEditing: props.isEditing,
        },
      );
      return;
    }

    // A viewport change while the overlay was hidden deferred its refresh rather than rebuild an
    // editor nobody could see (see runLayoutRefresh). Apply it now the editor is visible again so it
    // is fitted to the box the viewport settled on — but only through the SAME gates the scheduled
    // path honours: an undecoded image is left for the annotationImage onload handler (which
    // re-initializes from the decode itself), and an in-flight save/confirm/render keeps the refresh
    // deferred for the flush watcher below, which is exactly what that watcher exists for
    // (LSP-13917, break-it round 3).
    if (
      editor.value &&
      pendingLayoutRefresh.value &&
      annotationImageLoaded.value &&
      !layoutRefreshBlocked.value
    ) {
      void applyLayoutRefresh("isEditing reopen layout refresh");
      return;
    }

    // A parent that mounts closed (isEditing=false) and later flips it true — e.g. a lightbox's
    // click-to-annotate — needs the editor created here: the annotationImage onload handler that
    // normally does this only fires once per image load and won't refire just because isEditing
    // changed, so without this the editor stays permanently null.
    if (!editor.value) {
      // A re-initialization measures the current fit box itself, so any refresh deferred OR still
      // scheduled while the overlay was hidden is already satisfied by the paths below. Both halves
      // must go: an armed settle run would otherwise fire straight after this reopen and replace the
      // MarkerArea it just built (break-it round 2).
      discardQueuedLayoutRefresh();
      if (!currentImageUrl.value && props.targetImage) {
        void updateTargetImage(props.targetImage, true, false, true).catch(
          (err) => {
            reportTargetImageFailure(err, "isEditing reopen target image");
          },
        );
        return;
      }
      if (!annotationImageLoaded.value) {
        console.warn(
          "ImageEditor: isEditing reopen is reloading an image whose prior decode was invalidated",
          {
            conversationId: props.conversationId,
            hasTargetImage: Boolean(props.targetImage),
            hasCurrentImageUrl: Boolean(currentImageUrl.value),
          },
        );
        const imageToReload = props.targetImage ?? currentImageUrl.value;
        if (!imageToReload) {
          reportTargetImageFailure(
            new Error(
              "ImageEditor cannot reopen because no image is available to reload",
            ),
            "isEditing reopen target image",
          );
          return;
        }
        void updateTargetImage(imageToReload, true, false, true).catch(
          (err) => {
            reportTargetImageFailure(err, "isEditing reopen target image");
          },
        );
        return;
      }
      const initialized = initializeEditor("isEditing reopen");
      if (!initialized) return;
      setDimensions();
    }
  },
  { immediate: true },
);

// Flushes work deferred by the guards above once the in-flight crop save/confirm dialog (or a
// crop-init render — onOrientationChange/onWindowResize also defer pendingLayoutRefresh solely on
// cropperInitializing, which another save/confirmation guard clearing does not imply) finishes, so an
// orientation/resize refresh, an external image swap, or a parent-triggered teardown that arrived
// mid-operation still applies instead of being silently dropped or left stranded until some later,
// unrelated save/confirmation transition happens to flush it. Teardown wins over a
// pending image swap or layout refresh (the editor session is ending); a pending image swap already
// re-renders and re-measures via its own onload handler, so it supersedes a pending layout refresh.
watch(
  [
    isSaving,
    confirmingCropDiscard,
    confirmingEditorDiscard,
    cropperInitializing,
    annotationRestorePending,
  ],
  ([saving, confirmingCrop, confirmingEditor, initializing, restoring]) => {
    if (
      saving ||
      confirmingCrop ||
      confirmingEditor ||
      initializing ||
      restoring
    )
      return;

    if (pendingEditingTeardown.value) {
      pendingEditingTeardown.value = false;

      // isEditing can flip back to true while the teardown was deferred (e.g. a foreground interrupt
      // closes the session mid-save, then the user reopens it before the save finishes) — the isEditing
      // watcher's own false->true branch already re-initialized the editor for that reopened session, so
      // tearing it down here would silently kill a live session instead of a closed one. Fall through
      // to the pendingTargetImage/pendingLayoutRefresh checks below instead of returning, so a swap or
      // refresh queued during the same deferred window isn't stranded on the reopened session.
      if (!props.isEditing) {
        // Apply (not discard) a queued image swap even though the session is ending — currentImageUrl
        // must stay in sync with props.targetImage regardless of editing state, or a later reopen with
        // no further targetImage change would keep showing this stale, already-superseded photo (the
        // targetImage watcher only re-fires on a NEW change, not a change that already happened once).
        const discardedTargetImage = pendingTargetImage.value;
        pendingTargetImage.value = undefined;
        // pendingLayoutRefresh is cleared inside tearDownForEditingClosed() itself.
        tearDownForEditingClosed();

        if (discardedTargetImage !== undefined) {
          void updateTargetImage(discardedTargetImage, true, false, true).catch(
            (err) => {
              reportTargetImageFailure(
                err,
                "pendingTargetImage flush (teardown)",
              );
            },
          );
        }
        return;
      }
    }

    if (pendingTargetImage.value !== undefined) {
      const image = pendingTargetImage.value;
      pendingTargetImage.value = undefined;
      pendingLayoutRefresh.value = false;
      void updateTargetImage(image, true, false, true).catch((err) => {
        reportTargetImageFailure(err, "pendingTargetImage flush");
      });
      return;
    }

    if (pendingLayoutRefresh.value) {
      // Same still-decoding race as onOrientationChange/onWindowResize — a save/confirmation guard
      // clearing doesn't mean the annotation image has finished decoding. Leave the flag set; the
      // annotationImage onload handler flushes it once decoding finishes while editing, or clears it
      // when the editor has already closed.
      if (!annotationImageLoaded.value) {
        console.warn(
          "ImageEditor: pendingLayoutRefresh flush deferred — annotation image is still decoding",
          {
            conversationId: props.conversationId,
            isEditing: props.isEditing,
          },
        );
        return;
      }
      void applyLayoutRefresh("pendingLayoutRefresh flush");
    }
  },
);

watch(
  () => props.targetHeight,
  () => {
    setDimensions();
  },
);

watch(
  () => props.targetWidth,
  () => {
    setDimensions();
  },
);
</script>

<style lang="scss" scoped>
.editor-cancel {
  color: rgb(var(--v-theme-editorMenuButtonBorder));
}

.border-top {
  border-top: solid 2px #000 !important;
}

.border-side {
  border-left: solid 2px #000 !important;
}

cropper-shade {
  display: none;
}

.editor-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 2000;
}

.annotation-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

#crop-container {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 200;
  background: #000;

  :deep(cropper-canvas) {
    height: 100% !important;
    width: 100% !important;
    background-repeat: repeat;
  }
}

#marker-container {
  width: 100vw;
  height: 100%;
  background: #000;
  /* LSP-13917: no pinch/double-tap zoom on the editor surface — see initializeEditor()'s
	   canvasStyle for the matching rule inside the MarkerArea shadow root. */
  touch-action: none;
}

#marker-container.marker-interaction-disabled {
  pointer-events: none;
}

#marker-container :deep(img) {
  object-fit: contain;
  max-width: 100% !important;
  height: auto;
  display: block;
}

.close-editing {
  top: 0;
  right: 0;
  position: absolute;
  z-index: 1001;
}

.rounded-ts-circle {
  border-top-left-radius: 50% !important;
}

.rounded-bs-circle {
  border-bottom-left-radius: 50% !important;
}

.rounded-te-circle {
  border-top-right-radius: 50% !important;
}

.rounded-be-circle {
  border-bottom-right-radius: 50% !important;
}

.editor-menu .v-btn,
.close-editing {
  background-color: #fff !important;
  height: 44px !important;
  width: 44px !important;
}

/* LSP-16460: the crop-cancel control keeps its "Cancel Crop" label, so it must size to
	   its text instead of being forced into the shared 44px circle (which clipped it to "ncel C").
	   Higher specificity than .close-editing so width:auto wins over the 44px rule above. */
.close-editing.cancel-crop {
  width: auto !important;
  min-width: max-content;
  padding-inline: 1rem !important;
  line-height: 44px;
}

.editor-menu .v-btn:not(.close-editing, .close-menu) {
  border-radius: 0;
}

.editor-menu .v-btn.selected {
  background-color: #aaa !important;
}

.editor-menu .border-primary {
  border-color: #000 !important;
}

.editor-menu .v-btn.elevation-3 {
  box-shadow:
    0 4px 8px -1px rgba(0, 0, 0, 0.2),
    0 2px 4px -1px rgba(0, 0, 0, 0.14) !important;
}

.editor-menu .v-btn :deep(i) {
  color: v-bind(annotationColor) !important;
}

.editor-menu .crop.v-btn,
.editor-menu .crop :deep(i),
.editor-menu .rotate.v-btn,
.editor-menu .rotate :deep(i),
.editor-menu .trash.v-btn,
.editor-menu .trash :deep(i),
.editor-menu .undo.v-btn,
.editor-menu .undo :deep(i) {
  color: #000 !important;
}

.editor-menu .close-editing.v-btn,
.editor-menu .close-editing.v-btn :deep(i),
.editor-menu .close-menu.v-btn,
.editor-menu .close-menu.v-btn :deep(i) {
  color: #000 !important;
}

.editor-menu .v-btn {
  color: v-bind(annotationColor) !important;
  min-width: unset;
  border: none;
}

.editor-menu .v-btn.v-btn--disabled,
.editor-menu .v-btn.disabled {
  background-color: #f3f5f9 !important;
  color: rgba(0, 0, 0, 0.38) !important;
  opacity: 1;
  box-shadow: none !important;
}

.editor-menu .v-btn.v-btn--disabled :deep(.v-icon),
.editor-menu .v-btn.v-btn--disabled :deep(i),
.editor-menu .v-btn.v-btn--disabled :deep(svg),
.editor-menu .v-btn.disabled :deep(.v-icon),
.editor-menu .v-btn.disabled :deep(i),
.editor-menu .v-btn.disabled :deep(svg) {
  color: rgba(0, 0, 0, 0.38) !important;
}

.bottom-container {
  position: absolute;
  bottom: 0;
  z-index: 250;
  left: 50%;
  transform: translateX(-50%);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* Admin's button typography, explicit so the host app's Vuetify settings cannot change Cancel/Save. */
.bottom-container .v-btn {
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
}
/* The label sits in .v-btn__content, which async's base.css `*` reset sets to normal weight. */
.bottom-container .v-btn :deep(.v-btn__content) {
  font-weight: inherit;
}

.v-btn.disabled {
  :deep(.v-icon) {
    color: #aaa;
  }
}

.v-btn--disabled {
  :deep(.v-btn__overlay) {
    background-color: #fff;
  }
}

/* LSP-16034: a disabled Save must LOOK disabled. This rule used to repaint it in full primary
   blue at 0.6 opacity, which on a phone is indistinguishable from the enabled button - so a
   genuinely-disabled Save read as "the button is broken" rather than "there is nothing to save".
   `pointer-events: none` is kept: a disabled v-btn fires no click either way, and dropping it only
   changes the cursor. */
#save-image.v-btn--disabled {
  opacity: 0.5 !important;
  background-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  color: rgba(var(--v-theme-on-surface), 0.38) !important;
  pointer-events: none;

  :deep(.v-btn__overlay) {
    background-color: transparent !important;
  }
}

.image-progress {
  z-index: 10000;
  position: absolute;
  top: calc(50% - 36px);
  left: calc(50% - 36px);
}
</style>
